import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { fmtBkk } from '../../utils/bkkTime';
import { useTranslation } from 'react-i18next';
import { useStore, UNDO_SEND_SECS } from '../../store';
import { format, isThisYear, isToday } from 'date-fns';
import { fmtDate } from '../../utils/region';
import { Attachment, Email, EmailAiAnswer, EmailDensity, EmailFilter } from '../../types';
import { api } from '../../utils/api';
import ComposeNew from '../ComposeNew';
import AddToLeadModal from '../Leads/AddToLeadModal';
import { fileIcon, AttachmentPreview } from '../EmailDetail';
import OpenedBadge from '../OpenedBadge';
import { EMAIL_DRAG_TYPE } from '../../utils/emailDrag';
import { ACCOUNT_COLORS, tint } from '../../utils/accountColors';
import { PAGE_INSET } from '../../tabPalette';   // the one page gutter (report 76a2c198)

const LANG_COLORS: Record<string, { bg: string; color: string }> = {
  en: { bg: '#E6F1FB', color: '#185FA5' }, ja: { bg: '#FAEEDA', color: '#854F0B' },
  de: { bg: '#EAF3DE', color: '#3B6D11' }, fr: { bg: '#FAECE7', color: '#993C1D' },
  zh: { bg: '#EEEDFE', color: '#534AB7' }, ko: { bg: '#FBEAF0', color: '#72243E' },
  th: { bg: '#FAEEDA', color: '#633806' },
};

// Today: the time alone. Yesterday and earlier: the date AND the time, on the same clock as the
// reading pane — no year for this year's dates ('22/09 09:46'), the year only for another year's
// ('30/12/2025 09:46'). Calem 2026-09-24 (Bugify #594776d4, screenshot-8: "No need to include the
// year unless it's a different year from the current year"): at every list width — this year's year
// used to drop only once the list was narrower than 320px (split screen) or 640px (one-line rows).
// Day first, UK order (Calem, Bugify #dc76fc2f: "All dates in the system … in UK date format, not US").
function formatDate(date: Date): string {
  if (isToday(date)) return format(date, 'HH:mm');
  return format(date, isThisYear(date) ? 'dd/MM HH:mm' : 'dd/MM/yyyy HH:mm');
}
// A snooze's wake-up moment is chosen in the tenant's zone (EmailDetail), so it is shown in that zone
// too — not the browser's zone like the received dates above.
const formatBkk = (date: Date, lang: string) => fmtBkk(date.getTime(), lang);

// The list's unread rows wear their mailbox's own colour, the one on its badge in the sidebar, as
// the search bubble and the "+" wear the open mailbox's: washed with it, their left bar, dot,
// thread count and date in it. Calem 2026-10-01 (Bugify #87ed6d43: "Change the email theme color
// to the email client color as highlighted below", the highlight on the mailbox's badge): report
// a903bda5 had put all of them in the top menu's Email purple (#7F77DD) whichever mailbox was open.
// A plain read row has no colour of its own: it is see-through, so the list's background shows, as
// it does under the last row (Calem, #87ed6d43's 2nd opening: "Remove the background blue (and
// other email client colors) color from read emails. Make the background the same as the
// background of the page"). Its 1st opening had laid a pale wash of the mailbox's colour on it, and
// a903bda5 the Email purple's wash two shades lighter (rowTint). Open, ticked, lead, starred and
// archived rows keep their own colours, as they are coloured for a reason (see bg below).

function EmailItem({
  email, isActive, isDrafts, isSnoozedView = false, isLabelView = false, onEditDraft, selected, focused, selectMode, onToggleSelect, dragIds, splitScreen, density, onPreviewAtt,
}: {
  email: Email; isActive: boolean; isDrafts: boolean; isSnoozedView?: boolean; isLabelView?: boolean; onEditDraft: (email: Email) => void;
  selected: boolean; focused: boolean; selectMode: boolean; onToggleSelect: (id: string, shift: boolean) => void;
  dragIds: (id: string) => string[]; splitScreen: boolean; density: EmailDensity;
  onPreviewAtt: (p: { emailId: string; att: Attachment }) => void;
}) {
  const { setActiveEmail, markRead, markUnread, setStar, deleteEmails, showToast, me, loadEmails, accounts, uiLang } = useStore();
  const { t } = useTranslation();
  const canLead = me?.role === 'admin' || !!me?.modules?.includes('leads');
  const lc = LANG_COLORS[email.lang] || { bg: '#F1EFE8', color: '#5F5E5A' };
  const [hovered, setHovered] = useState(false);
  const [addLead, setAddLead] = useState(false);

  const unread = email.unread && !isDrafts;
  const starred = email.starred;
  const isLead = !!email.lead;
  // Not in a label view: everything filed there is 'archive' too, so the archive dress would
  // cover the whole list (see leftBar/bg below).
  const isArchive = email.folder === 'archive' && !isLabelView;
  // The row's mailbox colour — the one on its badge in the sidebar (teal if the mailbox isn't in the
  // viewer's list).
  const acct = accounts.find(a => a.id === email.accountId);
  const acctColor = ((acct && ACCOUNT_COLORS[acct.color]) || ACCOUNT_COLORS.teal).accent;
  const [leadPop, setLeadPop] = useState<{ x: number; y: number } | null>(null);

  // Track click timing to distinguish single vs double click
  const clickTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClick = (e: React.MouseEvent) => {
    if (isDrafts) {
      if (clickTimer.current) clearTimeout(clickTimer.current);
      clickTimer.current = setTimeout(() => setActiveEmail(email.id), 200);
    } else {
      setActiveEmail(email.id);
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDrafts) {
      if (clickTimer.current) { clearTimeout(clickTimer.current); clickTimer.current = null; }
      onEditDraft(email);
    }
  };

  // Colour priority: active > unread > lead (purple) > starred (amber) > archive (gray) > normal.
  const bg = isActive
    ? 'var(--bg-active)'
    : unread
      ? tint(acctColor, 0.08)          // mailbox-colour tint for unread
      : isLead
        ? 'rgba(109,40,217,0.07)'      // purple tint for lead emails
        : starred
          ? 'rgba(245,166,35,0.07)'    // amber tint for starred
          : isArchive
            ? 'rgba(107,114,128,0.07)' // gray tint for archived
            : 'transparent';           // read: no tint, the list's background shows
  // Left bar: the open row's and a lead's are the row's own mailbox colour, the one on its unread
  // dot; a starred row's amber, an archived row's grey.
  // Calem, Bugify #722b880d ("Shouldn't this be green?"): the open row's bar was var(--accent), the
  // colour of the mailbox open in the sidebar. Its 3rd opening: both rows circled in screenshot-3
  // are leads ("The purple colored vertical bar highlighted in the picture should be green for that
  // email address"); a lead's bar was the Leads purple #6D28D9, an unread lead's too. A lead still
  // stands out by its purple target icon and, once read, its purple wash (bg above). A ticked row's
  // bar stays var(--accent) (the row's boxShadow below).
  // An unread row draws NO bar (Calem, report 75cd6e0e, 5th opening: "remove the extra thick blue
  // lines on the left-hand side of the bubble in Unread"). Every row in the UNREAD bubble is unread,
  // so each painted the mailbox colour 3px wide right beside that bubble's own 1px line in the same
  // colour (emailCardLine) — one thick stripe down the inside of the bubble. The rows keep their
  // mailbox tint, their coloured dot and their bold text, which is what marks them unread.
  // A row in a LABEL view wears NO archive dress at all — neither the grey bar nor the grey tint
  // (Calem, report 7f03a1bd: "remove the thick left border as shown below", then its 2nd opening:
  // "remove background from everything else"). On Gmail a labelled email sits in All Mail, and sync
  // files All Mail and every label folder as 'archive' (routes/emails.ts), so every row of a label
  // was an archived row: the grey stripe ran the whole list down and the grey wash lay under every
  // row. Opening #1 dropped the stripe here; #2 drops the wash with it, by letting isLabelView
  // clear isArchive itself (above) so the bg chain falls through to 'transparent'. A plain
  // labelled row now reads exactly like a normal read row — same dot, sender, subject and preview.
  // An open, unread, lead or starred row in a label keeps its own tint and bar, and the Archive
  // folder and the lists that mix folders (Starred, a search) are untouched.
  const leftBar = isActive || isLead
    ? acctColor
    : starred
      ? '#F5A623'
      : isArchive
        ? '#9CA3AF'
        : 'transparent';
  // The row's fill: a ticked row's accent wash, else the tint above. The hover buttons (split
  // screen off) sit on a strip of the same colour, so the two can't drift apart.
  const rowBg = selected ? 'color-mix(in srgb, var(--accent) 16%, transparent)' : bg;
  const LEAD_ST: Record<string, { bg: string; color: string; label: string }> = {
    open: { bg: '#E4F0FB', color: '#2B6CB0', label: 'Open' },
    won: { bg: '#DCFCE7', color: '#15803D', label: 'Won' },
    lost: { bg: '#FCE7E7', color: '#B91C1C', label: 'Lost' },
  };
  const lst = (email.lead && LEAD_ST[email.lead.status]) || LEAD_ST.open;

  const showCheckbox = !isDrafts && (selectMode || selected || hovered);
  const langBadge = !isDrafts && (
    <span style={{ display: 'inline-flex', fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 10, marginRight: 5, letterSpacing: '0.04em', background: lc.bg, color: lc.color }}>{email.lang.toUpperCase()}</span>
  );
  // A Sent email the recipient has opened — its read receipt came back: Quotations' circle, straight
  // after the language badge where the subject starts (Calem, Bugify #fcd1ede7, 5th opening: "Move the
  // read receipt notification to the location shown with the arrow below"); it was at the end of the
  // row, after the time. Inline and middle-aligned, as OpenedBadge draws it, so it sits level with the
  // language badge, 5px before the subject as that badge is. Its 19px is more than the subject's line
  // is tall, so negative 4px margins above and below keep it from making the line (and the row)
  // taller, and each subject line below reaches 4px further up and down than it is laid out (padding,
  // taken back by a negative margin) so the clipping that ends a long subject in "…" can't cut it off.
  const opened = <OpenedBadge at={email.openedAt} tipKey="eml_opened_tip" style={{ marginRight: 5, marginTop: -4, marginBottom: -4 }} />;
  const subject = email.subject || '(No subject)';
  const draftHint = <><i className="ti ti-pencil" style={{ fontSize: 10 }} aria-hidden />Double-click to continue editing</>;
  const preview = !isDrafts && ((email as any).preview || email.bodyOriginal.replace(/<[^>]+>/g, '').slice(0, 80));
  // Split screen off: the hover buttons end where the Subject column ends, just before the Body.
  // That column is a share of the row's width and shifts with the tick boxes' indent, so its right
  // edge is read off the page on hover (and again if the indent comes or goes meanwhile).
  const subjectRef = useRef<HTMLDivElement>(null);
  const [subjectEnd, setSubjectEnd] = useState(0);
  useLayoutEffect(() => {
    const s = subjectRef.current;
    if (hovered && s) setSubjectEnd(s.offsetLeft + s.offsetWidth);
  }, [hovered, selectMode, splitScreen]);
  // Row padding (top/bottom), by the toolbar's Density. Default: enough air between rows for the
  // larger text, plus 1px each side (Calem 2026-09-24, Bugify #594776d4); the one-line full-width
  // list a little tighter than the three-line column. Comfortable: 4px more above and below.
  // Compact: the least that still holds the 24px hover buttons in a one-line row — and the
  // split-screen row drops its preview line (below).
  const padY = density === 'comfortable' ? (splitScreen ? 14 : 12) : density === 'compact' ? 4 : (splitScreen ? 10 : 8);
  // Default density also lists the email's attachments as chips on a line of their own under the
  // text (Gmail-style): 4px gap + 24px chips. Comfortable and Compact keep just the paperclip.
  const showChips = density === 'default' && email.attachments.length > 0;
  const chipsH = showChips ? 28 : 0;
  // In the one-line row the tick box and the hover buttons sit in the vertical centre of its text
  // line (the chips line, if any, below it); in the three-line row they follow padY, level with the
  // sender line and the preview line (the subject in Compact) respectively — never over the chips.
  const midRow = { top: chipsH ? `calc(50% - ${chipsH / 2}px)` : '50%', transform: 'translateY(-50%)' };
  return (
    <div
      id={'eml-' + email.id}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative' }}
    >
      {/* Selection checkbox (appears on hover / once any row is selected). */}
      {showCheckbox && (
        <span
          onClick={e => { e.stopPropagation(); onToggleSelect(email.id, (e as any).shiftKey); }}
          title="Select"
          style={{ position: 'absolute', left: 11, ...(splitScreen ? { top: padY + 2 } : midRow), zIndex: 3, width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--border-strong)'}`, background: selected ? 'var(--accent)' : 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          {selected && <i className="ti ti-check" style={{ fontSize: 11, color: '#fff' }} aria-hidden />}
        </span>
      )}
      {/* Draggable onto a label in the sidebar to apply it. Set on this button rather than the
          wrapper, which also holds the inline Add-to-Lead modal. Drafts have no row actions. */}
      <button
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        draggable={!isDrafts}
        onDragStart={isDrafts ? undefined : e => {
          e.dataTransfer.setData(EMAIL_DRAG_TYPE, JSON.stringify(dragIds(email.id)));
          e.dataTransfer.effectAllowed = 'copy';
        }}
        title={isDrafts ? 'Double-click to edit draft' : undefined}
        style={{
          display: 'block', width: '100%', padding: `${padY}px 14px ${padY}px ${selectMode ? 34 : 16}px`,
          border: 'none',
          borderBottom: '1px solid var(--border)',
          background: rowBg,
          boxShadow: `inset 3px 0 0 ${selected ? 'var(--accent)' : leftBar}${focused ? ', inset 0 0 0 2px var(--accent)' : ''}`,
          cursor: 'pointer',
          textAlign: 'left' as const, position: 'relative' as const,
        }}
      >
        {/* Split screen on: sender + time above the subject and preview lines. Split screen off
            (full-width list): the whole email is ONE line — From, Subject and Body as columns kept
            apart by space alone (no separator lines), then the icons + time. Its hover buttons cover
            the right end of the Subject column (below). */}
        <div style={splitScreen
          ? { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1 }
          : { display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, ...(splitScreen ? {} : { width: 190, flexShrink: 0 }) }}>
            {/* Leading dot on every row, so all senders start at the same point: the mailbox colour
                when unread, light grey once read. Drafts have no read state, so no dot. */}
            {!isDrafts && <span style={{ width: 8, height: 8, borderRadius: '50%', background: unread ? acctColor : 'var(--border-strong)', flexShrink: 0 }} aria-label={unread ? 'unread' : undefined} />}
            {isDrafts && (
              <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8, background: '#FFF3CD', color: '#7B5200', border: '1px solid #F5C842', letterSpacing: '0.04em' }}>DRAFT</span>
            )}
            <span style={{ fontSize: 14.5, fontWeight: unread ? 700 : (isDrafts ? 600 : 500), color: unread ? 'var(--text-primary)' : 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isDrafts ? (email.to.join(', ') || 'No recipients')
                : ((email.threadCount || 1) > 1 && email.participants?.length
                    ? email.participants.map(p => (p.name || p.email).split(' ')[0]).join(', ')
                    : (email.from.name || email.from.email))}
            </span>
            {/* Conversation size — Gmail-style count next to the participants */}
            {!isDrafts && (email.threadCount || 1) > 1 && (
              <span style={{ fontSize: 11.5, fontWeight: 600, color: unread ? acctColor : 'var(--text-tertiary)', flexShrink: 0 }}>{email.threadCount}</span>
            )}
          </div>
          {!splitScreen && (<>
            {/* A fixed share of the row, so every row's Body column starts at the same point. Its 4px
                of padding above and below, taken back by the margin, is room for the opened circle
                (see opened, above). */}
            <div ref={subjectRef} style={{ flex: '0 1 32%', minWidth: 0, padding: '4px 0', margin: '-4px 0', fontSize: 13.5, fontWeight: unread ? 700 : 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {langBadge}
              {opened}
              {subject}
            </div>
            <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isDrafts
                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{draftHint}</span>
                : preview
              }
            </div>
          </>)}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            {isLead && (
              <span
                onMouseEnter={e => { const r = e.currentTarget.getBoundingClientRect(); setLeadPop({ x: r.right, y: r.bottom }); }}
                onMouseLeave={() => setLeadPop(null)}
                onClick={e => { e.stopPropagation(); setLeadPop(null); setAddLead(true); }}
                title={t('ld_change_or_remove')}
                style={{ display: 'inline-flex', cursor: 'pointer' }}
              >
                <i className="ti ti-target-arrow" style={{ fontSize: 13, color: '#6D28D9' }} aria-hidden />
              </span>
            )}
            {starred && <i className="ti ti-star-filled" style={{ fontSize: 13, color: '#F5A623' }} aria-hidden />}
            {email.attachments.length > 0 && <i className="ti ti-paperclip" style={{ fontSize: 11, color: 'var(--text-tertiary)' }} aria-hidden />}
            {/* Snoozed folder: the time it comes BACK, not the received date (owner review 2026-09-24). */}
            {isSnoozedView && email.snoozedUntil
              ? <span title={t('eml_snooze_returns')} style={{ fontSize: 10, color: '#B45309', fontWeight: 600, whiteSpace: 'nowrap' }}><i className="ti ti-clock-pause" style={{ fontSize: 10 }} aria-hidden /> {formatBkk(email.snoozedUntil, uiLang)}</span>
              : <span style={{ fontSize: 10, color: unread ? acctColor : 'var(--text-tertiary)', fontWeight: unread ? 600 : 400, whiteSpace: 'nowrap' }}>{formatDate(email.date)}</span>}
          </div>
        </div>
        {splitScreen && (<>
          {/* The same 4px of room for the opened circle as in the one-line row; -3px below (its 1px gap
              to the line under it, less the 4px) keeps that line where it was. */}
          <div style={{ fontSize: 13.5, color: unread ? 'var(--text-primary)' : 'var(--text-secondary)', padding: '4px 0', margin: '-4px 0 -3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: unread ? 600 : (isDrafts ? 500 : 400) }}>
            {langBadge}
            {opened}
            {subject}
          </div>
          {/* Compact density: sender and subject only — no preview line */}
          {density !== 'compact' && (
            <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isDrafts
                ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{draftHint}</span>
                : preview
              }
            </div>
          )}
        </>)}
        {/* Attachment chips (Default density): under the preview line, or in the one-line row under
            its Subject and Body columns (past the 190px From column + 16px gap). The first three by
            name, then '+N'. A chip previews its file over the page, with a Download button there
            (Calem, Bugify #e0e30d4b: preview, don't download); '+N' just opens the email like the row. */}
        {showChips && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, height: 24, overflow: 'hidden', paddingLeft: splitScreen ? 0 : 190 + 16 }}>
            {email.attachments.slice(0, 3).map(att => (
              <span
                key={att.id}
                onClick={e => { e.stopPropagation(); onPreviewAtt({ emailId: email.id, att }); }}
                title={t('eml_att_preview', { name: att.name })}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 24, minWidth: 0, maxWidth: 180, padding: '0 10px 0 8px', borderRadius: 999, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', cursor: 'pointer' }}
              >
                <i className={`ti ${fileIcon(att.type || '')}`} style={{ fontSize: 14, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{att.name}</span>
              </span>
            ))}
            {email.attachments.length > 3 && (
              <span title={t('eml_att_more', { n: email.attachments.length - 3 })} style={{ display: 'inline-flex', alignItems: 'center', height: 24, padding: '0 9px', borderRadius: 999, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', flexShrink: 0 }}>
                +{email.attachments.length - 3}
              </span>
            )}
          </div>
        )}
      </button>

      {/* Hover quick-actions: read/unread toggle, star, (lead,) delete. Split screen on: at the
          right end, level with the row's preview line. Off: over the right end of the Subject
          column, centred on the one-line row's text, on a strip of the row's own colour (laid over
          the list's panel, as the row's tints are see-through) so the subject's end is hidden
          cleanly rather than showing between the buttons. Both above the attachment chips, so the
          chips stay clickable. Each icon is 15.73px in its 24px button (Calem, Bugify #f773cce8
          opening #2, "Make them another 10% larger" — a second 10% off the 14.3px opening #1 had
          set, which was itself 10% of the glyph's own 13px, off #f72c881e's "Make these icons 1px
          larger" from 12px; 'n% larger' is always n% of the item's own size). The button, its 4px
          gap and the strip behind it never change: a 15.73px glyph still leaves ~4.1px of ring, so
          nothing clips and Compact density (padY 4, sized to hold the 24px buttons) stays put. */}
      {hovered && !isDrafts && (
        <div style={{ position: 'absolute', display: 'flex', gap: 4, ...(splitScreen
          ? { right: 10, bottom: padY - 3 + chipsH }
          : { ...midRow, right: `calc(100% - ${subjectEnd}px)`, paddingLeft: 8, backgroundColor: 'var(--bg-panel)', backgroundImage: `linear-gradient(${rowBg}, ${rowBg})` }) }}>
          <button
            onClick={e => { e.stopPropagation(); if (email.unread) { (email.threadIds?.length ? email.threadIds : [email.id]).forEach(id => markRead(id)); } else { markUnread(email.id); } }}
            title={email.unread ? 'Mark as read' : 'Mark as unread'}
            style={{ width: 24, height: 24, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-sm)' }}
          >
            <i className={`ti ${email.unread ? 'ti-mail-opened' : 'ti-mail'}`} style={{ fontSize: 15.73 }} aria-hidden />
          </button>
          <button
            onClick={e => { e.stopPropagation(); const ids = email.threadIds?.length ? email.threadIds : [email.id]; const on = !starred; ids.forEach(id => setStar(id, on)); }}
            title={starred ? 'Unstar' : 'Star'}
            style={{ width: 24, height: 24, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: starred ? '#F5A623' : 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-sm)' }}
          >
            <i className={`ti ti-star${starred ? '-filled' : ''}`} style={{ fontSize: 15.73 }} aria-hidden />
          </button>
          {canLead && (
            <button
              onClick={e => { e.stopPropagation(); setAddLead(true); }}
              title={isLead ? t('ld_change_or_remove') : t('ld_add_to')}
              style={{ width: 24, height: 24, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: '#6D28D9', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-sm)' }}
            >
              <i className="ti ti-target-arrow" style={{ fontSize: 15.73 }} aria-hidden />
            </button>
          )}
          {/* Delete the whole conversation — same confirm as the bulk Delete; the row leaves the
              list at once and comes back only if the server can't delete it (see store.deleteEmails). */}
          <button
            onClick={e => { e.stopPropagation(); const ids = email.threadIds?.length ? email.threadIds : [email.id]; if (window.confirm(t('em_del_confirm', { n: ids.length }))) void deleteEmails(ids); }}
            title={t('delete')}
            style={{ width: 24, height: 24, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: '#E24B4A', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-sm)' }}
          >
            <i className="ti ti-trash" style={{ fontSize: 15.73 }} aria-hidden />
          </button>
        </div>
      )}
      {addLead && <AddToLeadModal email={{ id: email.id, subject: email.subject, fromName: email.from.name || '', fromEmail: email.from.email || '', date: email.date, bodyText: (email as any).preview || email.bodyOriginal || '' }}
        current={email.lead ? { rowId: email.lead.rowId, id: email.lead.id, name: email.lead.name, company: email.lead.company } : undefined}
        onClose={() => setAddLead(false)} onDone={() => { showToast(t('ld_added')); void loadEmails({ silent: true } as any); }} />}
      {/* Lead hover card — basic info; fixed-position so it isn't clipped by the list scroll area. */}
      {leadPop && email.lead && (
        <div style={{ position: 'fixed', left: Math.min(leadPop.x, window.innerWidth - 256), top: leadPop.y + 5, zIndex: 4000, width: 244, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-md)', padding: '10px 12px', fontSize: 12.5, fontFamily: 'inherit', pointerEvents: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <i className="ti ti-target-arrow" style={{ fontSize: 13, color: '#6D28D9' }} aria-hidden />
            <span style={{ fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email.lead.name || email.lead.company || '—'}</span>
            <span style={{ marginLeft: 'auto', fontSize: 9.5, fontWeight: 700, padding: '1px 7px', borderRadius: 8, background: lst.bg, color: lst.color, flexShrink: 0 }}>{lst.label}</span>
          </div>
          {email.lead.company && email.lead.name && <div style={{ color: 'var(--text-secondary)', marginBottom: 2 }}>{email.lead.company}</div>}
          {email.lead.owner_name && <div style={{ color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 5 }}><i className="ti ti-user" style={{ fontSize: 11 }} aria-hidden />{email.lead.owner_name}</div>}
          {email.lead.phone && <div style={{ color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 5 }}><i className="ti ti-phone" style={{ fontSize: 11 }} aria-hidden />{email.lead.phone}</div>}
          {email.lead.email && <div style={{ color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 5, wordBreak: 'break-all' }}><i className="ti ti-mail" style={{ fontSize: 11 }} aria-hidden />{email.lead.email}</div>}
          <div style={{ color: 'var(--text-tertiary)', marginTop: 4, fontSize: 11, opacity: 0.8 }}>{t('ld_source')}: {email.lead.source}</div>
        </div>
      )}
    </div>
  );
}

// The bubble each of this pane's regions is drawn as — the toolbar (`toolbar` below), the UNREAD group and
// the EVERYTHING ELSE group — since Calem, report 75cd6e0e ("Make one email client into bubbles as shown in
// the screenshot"), where all three were flat panes running edge to edge on one surface. Same three values
// as the sidebar's bubble (EMAIL_CARD_R / emailCardLine in Sidebar, where the look is written up): the 10px
// corners and 1px line of the Quotation / Bank / Petty Cash cards (CARD_R / cardLine there), the line in the
// open mailbox's own colour at 51% through var(--accent) (accentVars in MailApp), with page around each and
// between them — EMAIL_GAP down the sides and along the bottom, GROUP_GAP_TOP between, TOOLBAR_GAP_TOP above
// the first. Each keeps the fill it had — the toolbar its grey band, the rows their tints
// and the lit row its wash — so only the edges and the gaps are new.
// GROUP_BUBBLE clips (overflow) so the first and last rows' square corners take the bubble's.
// Its word — UNREAD, EVERYTHING ELSE — sits ON its top border (GroupBar), so it is drawn outside that clipping:
// GROUP_WRAP is the box each group bubble stands in, which holds the page the bubble used to hold itself and
// gives the word, absolutely placed, something to be centred on.
// Every group bubble stands the same GROUP_GAP_TOP of page below whatever is above it — the toolbar bubble for
// the first, the bubble before for the rest (adjacent margins collapse, so the bubble's own EMAIL_GAP below it
// is swallowed by the next one's GROUP_GAP_TOP above). Calem, report 75cd6e0e, 5th opening: "Decrease the
// UNREAD to EVERYTHING ELSE bubble space by 3 pixels and then make this new size space above the UNREAD bubble
// as well" — that space was 30 (10 of page + the read group's own 20, Bugify #df98616c), so 27, and
// UNREAD had only the 10 of page above it. 6th opening: "Reduce all highlighted spacing by 3px" — the
// two strokes marked those same two gaps, toolbar-to-UNREAD and UNREAD-to-EVERYTHING ELSE, which are the one
// GROUP_GAP_TOP, so 27 -> 24 and the collapsing margins keep the pair equal.
// 8th opening: "Reduce the highlighted space below by another 4 pixels", and a third mark, a curly bracket
// drawn in the white strip between the sidebar bubble and the left edge of the UNREAD bubble — "Yes please cut
// that strip by 4 as well". So the two strokes are GROUP_GAP_TOP 24 -> 20 (still the one constant, still equal),
// and the bracket is this pane's own side gutter, EMAIL_GAP 10 -> 6, which also takes the bubbles' bottom and
// the toolbar bubble's sides. That strip is 24 of its own make — the sidebar bubble's margin 10 + the 4px drag
// strip (DragHandle in MailApp) + this 10 — so the 4 comes off this side alone and the sidebar's own EMAIL_GAP
// stays 10; cutting both would have taken 8. The toolbar bubble's TOP margin is TOOLBAR_GAP_TOP, left at 10, so
// its top edge still lines up with the pinned sidebar bubble's: Calem marked the strip down the side, not the
// band above the toolbar.
// 9th opening: "Remove another 3 pixels from the same vertical space and the horizontal spaces" — the same two
// gutters again, so GROUP_GAP_TOP 20 -> 17 and EMAIL_GAP 6 -> 3, with TOOLBAR_GAP_TOP and the sidebar's own
// EMAIL_GAP left at 10 as in the 8th, so the strip between sidebar and list narrows by exactly 3 (10 + the 4px
// drag strip + this 3). GROUP_GAP_TOP of 17 still clears the 8px the GroupBar word rises above its bubble, by 9 —
// the tightest it has been, and a further cut starts crowding UNREAD against the toolbar bubble.
// Calem, report 3a24e49f ("Remove the read emails from their bubble and just allow them to float on the page in
// the exact same position they are now. Also, indent all bubbles from the right-hand side of the page match the
// left-hand side of the page. Keep the horizontal line that includes the words 'everything else' though"), two
// strokes, both here. ONE: the read group is no longer a closed bubble — GROUP_OPEN below keeps the one line its
// word stands on and drops the rest. The UNREAD group and the toolbar stay the bubbles they are: he marked the
// read bubble's own two sides and asked for the read emails only.
// TWO: the page down the bubbles' right, EMAIL_GAP's 3, becomes RIGHT_GAP's 17 to match the strip down their
// left. That strip is 17 of three makes — the sidebar bubble's own margin 10 + the 4px drag strip + this 3 — so
// matching it is not a matter of this gutter: it takes a right margin of 17 on whatever meets the page's right
// edge, and nothing else moves. Which is the full-width list's group bubbles (GROUP_WRAP_WIDE) and the toolbar
// bubble, that one in both layouts since split screen portals it into MailApp's strip across the whole page.
// Inside split screen the list column's bubbles keep EMAIL_GAP down their right, where the drag strip and the
// reading pane stand in place of page. GROUP_GAP_TOP, TOOLBAR_GAP_TOP and the sidebar's own EMAIL_GAP are
// untouched, so the 9th opening's floor above still stands.
// 2nd opening of 3a24e49f: "Move the 'EVERYTHING ELSE' line (and read emails) down by another 10 pixels." Only
// that run moves, so the 10 cannot go on GROUP_GAP_TOP, which every group carries — it would push UNREAD off the
// toolbar bubble too. It is the read group's own top margin: GROUP_GAP_TOP_READ, 27, carried by the wrapper
// (GROUP_WRAP_READ / GROUP_WRAP_WIDE_READ) in place of the 17 the UNREAD group keeps. The word rides that
// wrapper (GroupBar is placed on it), so the EVERYTHING ELSE line and every row below it come down together, and
// the gap between the two groups is 27 where the unread one still stands 17 below the toolbar.
// Calem, report d8e36715: "Move the unread bubble down the page another 10 pixels." The gap above UNREAD is the
// one the 9th opening above cut to 17, and it is the only gap left at 17 — the read group took its extra 10 in
// 3a24e49f's 2nd opening and the UNREAD group never did. So UNREAD gets its own top margin the same way the read
// group got one: GROUP_GAP_TOP_UNREAD, 27, carried by GROUP_WRAP_UNREAD / GROUP_WRAP_WIDE_UNREAD in place of the
// 17. GROUP_GAP_TOP itself stays 17, so the single bubble Drafts and Snoozed are drawn as (the `groups` key
// 'all', which is not the screen he marked) and the toolbar-to-bubble floor the 9th opening set are both
// untouched.
// The two read gaps are untouched as well: they are measured from the UNREAD bubble's BOTTOM, so EVERYTHING ELSE
// and all its rows ride the bubble's drop down with it and the gap between the two groups stays the 27 it was.
// 2nd opening of d8e36715: "UNREAD up by 3 pixels and everything else by 3 pixels." Three of that 10 come back,
// so GROUP_GAP_TOP_UNREAD went to 24 (GROUP_GAP_TOP + 7) and the toolbar-bubble-to-UNREAD strip lost 3. It stays the
// one constant, so both layouts and both states move together. Nothing else changes: the read gaps still measure
// from the UNREAD bubble's bottom, so EVERYTHING ELSE and every read row ride the same 3 up and the gap between
// the two groups is still 27 — which is "and everything else by 3 pixels". GROUP_GAP_TOP's own 17, now worn only
// by the single 'all' bubble of Drafts and Snoozed, and the 9th opening's floor above are left alone again.
// 3rd opening of d8e36715: "Bring the top bar across to the first column and bring the first column down to the
// level of read the email as per the screenshot below." That one is the module's layout, not these gaps — the
// toolbar bubble's strip is MailApp's own first row now, over the sidebar as well (see `toolbar` below). The bar
// still stands directly above the list, so every gap here is still read from it and not one of them moves.
// Follow-on to that opening — Calem, report d52103ad: "Bring the left (email account menu) down by 6 pixels and
// the unread bubble up to match it horizontally", two arrows on the screenshot, down on the sidebar card's top
// row and up on this bubble. With the bar a row of its own above both columns, the pinned sidebar card and this
// bubble hang from the same edge for the first time, and they hung 14px apart: the card at the sidebar's own
// EMAIL_GAP, 10, and this bubble at GROUP_GAP_TOP_UNREAD's 24. The arrows split that step, the card down 6
// (EMAIL_GAP_TOP in Sidebar) and this bubble up 8, so both tops land on 16. So GROUP_GAP_TOP_UNREAD is 16 —
// still the one constant, so both layouts and both states move together, and the held block's own measurement
// (holdUnread below) follows. The two read gaps are untouched once more: they measure from the UNREAD bubble's
// BOTTOM, so EVERYTHING ELSE and every read row ride the same 8 up and the gap between the two groups is still
// the 27 the earlier openings set. GROUP_GAP_TOP's own 17 stays as well, worn only by the single 'all' bubble
// of Drafts and Snoozed, which is not the screen the report marks. This is the one box to go under that 17, and the
// GroupBar word it carries rises 8px above the bubble's edge, so at 16 the word clears the bar by 8.
const EMAIL_CARD_R = 10;
const EMAIL_GAP = 3;
// The page down the bubbles' right where the list reaches the window's edge: PAGE_INSET, the one page gutter the whole
// system stands in by, 15px (Calem, report 76a2c198, 5th opening: "Move the left and right page spacing in from the
// edges to 15 px on every page of the SLF system"). It was 17, cut to match the strip down the bubbles' left — which
// is 17 of three makes, the sidebar card's own margin 10 + the 4px drag strip + this pane's 3 (report 3a24e49f, see
// above). That strip is between two columns of the module and is no page gutter; the page's left edge is the sidebar
// card's, which is on the same 15 now (EMAIL_GAP there, Sidebar), so 3a24e49f's "indent all bubbles from the
// right-hand side of the page match the left-hand side of the page" holds truer than before: both page edges are the
// one number. Calem's own bar is untouched beyond its gutters ("You do not need to change the email top bar as it is
// already a bubble").
const RIGHT_GAP = PAGE_INSET;
const GROUP_GAP_TOP = 17;
const GROUP_GAP_TOP_UNREAD = 16;
const GROUP_GAP_TOP_READ = GROUP_GAP_TOP + 10;
const TOOLBAR_GAP_TOP = 10;
// The gutter down the toolbar bubble's left, worn only where it is drawn in MailApp's strip across the whole
// module: the page's own, PAGE_INSET's 15, which is the sidebar card's left margin too (Sidebar) and not this
// pane's 3 (see `toolbar` below — Calem, report e4ff355f, 4th opening). In that strip the bubble stands on the
// page's left edge, where the sidebar bubble under it is the thing its edge has to line up with; the 3 is the strip
// between sidebar and list and belongs to the bubbles inside this column alone. It was 10, the sidebar card's gutter
// of the day; both read the one constant now (report 76a2c198, 5th opening — see RIGHT_GAP above).
const TOOLBAR_GAP_LEFT = PAGE_INSET;
// The toolbar bubble's own inside: 12px of padding down each side and 8px between the things standing on
// it. Named because the bulk-action icons are placed by measuring from the page's left edge in to where
// this bubble's content starts (bulkIndent below — report ba24c1ec's 3rd opening).
const TOOLBAR_PAD_X = 12;
const TOOLBAR_ITEM_GAP = 8;
// The gap the bulk-action icons keep from the count beside them, over and above the bar's own — and so the
// floor they can never come closer than, however far left the email bubbles' edge falls (bulkIndent below).
const BULK_GAP = 4;
// What the toolbar's Unsend switch means by On when it is turned on from Off (report eae213ee): the shortest
// wait the composer's Send menu offers, 5 seconds — the seconds pill beside it then changes it to 10 or 20.
const UNSEND_ON_SECS = UNDO_SEND_SECS.find(n => n > 0) || 0;
const emailCardLine = '1px solid color-mix(in srgb, var(--accent) 51%, transparent)';
const GROUP_WRAP: React.CSSProperties = { position: 'relative', margin: `${GROUP_GAP_TOP}px ${EMAIL_GAP}px ${EMAIL_GAP}px` };
// The same box with RIGHT_GAP of page down its right, for the list that runs to the page's right edge (split
// screen off). The whole margin is written as the one shorthand in both, never a marginRight laid over it: the
// two swap on the same elements when split screen is switched, and a longhand taken back off a shorthand leaves
// the side at 0 rather than at the shorthand's value.
const GROUP_WRAP_WIDE: React.CSSProperties = { ...GROUP_WRAP, margin: `${GROUP_GAP_TOP}px ${RIGHT_GAP}px ${EMAIL_GAP}px ${EMAIL_GAP}px` };
// The same two boxes for the UNREAD group alone, standing GROUP_GAP_TOP_UNREAD below the toolbar bubble instead
// of GROUP_GAP_TOP (report d8e36715, see above). The whole margin is the one shorthand here too, never a
// marginTop laid over GROUP_WRAP: these swap on the same element as split screen is switched, and a longhand
// taken back off a shorthand leaves the side at 0 rather than at the shorthand's value.
// They are worn in both states — the group inside the pane and the block held still above it — so the block
// stands at the same 16 either way.
const GROUP_WRAP_UNREAD: React.CSSProperties = { ...GROUP_WRAP, margin: `${GROUP_GAP_TOP_UNREAD}px ${EMAIL_GAP}px ${EMAIL_GAP}px` };
const GROUP_WRAP_WIDE_UNREAD: React.CSSProperties = { ...GROUP_WRAP, margin: `${GROUP_GAP_TOP_UNREAD}px ${RIGHT_GAP}px ${EMAIL_GAP}px ${EMAIL_GAP}px` };
// The same two boxes for the read group alone, standing GROUP_GAP_TOP_READ below the UNREAD bubble instead of
// GROUP_GAP_TOP (report 3a24e49f's 2nd opening, see above). Each writes its whole margin as the one shorthand
// too, for the same reason the pair above does: the four swap on the same element as split screen is switched.
const GROUP_WRAP_READ: React.CSSProperties = { ...GROUP_WRAP, margin: `${GROUP_GAP_TOP_READ}px ${EMAIL_GAP}px ${EMAIL_GAP}px` };
const GROUP_WRAP_WIDE_READ: React.CSSProperties = { ...GROUP_WRAP, margin: `${GROUP_GAP_TOP_READ}px ${RIGHT_GAP}px ${EMAIL_GAP}px ${EMAIL_GAP}px` };
// The same two once more, for while the UNREAD group is held out of the scrolling pane (GROUP_HELD below).
// There the two groups stand in different boxes, so their margins no longer collapse into the one gap: the
// held block's own EMAIL_GAP below it is real space of the column, and the read wrapper carries only what is
// left of GROUP_GAP_TOP_READ, so the read run still starts the same 27 below the unread bubble.
const GROUP_GAP_TOP_READ_HELD = GROUP_GAP_TOP_READ - EMAIL_GAP;
const GROUP_WRAP_READ_HELD: React.CSSProperties = { ...GROUP_WRAP, margin: `${GROUP_GAP_TOP_READ_HELD}px ${EMAIL_GAP}px ${EMAIL_GAP}px` };
const GROUP_WRAP_WIDE_READ_HELD: React.CSSProperties = { ...GROUP_WRAP, margin: `${GROUP_GAP_TOP_READ_HELD}px ${RIGHT_GAP}px ${EMAIL_GAP}px ${EMAIL_GAP}px` };
const GROUP_BUBBLE: React.CSSProperties = { borderRadius: EMAIL_CARD_R, border: emailCardLine, overflow: 'hidden' };
// The read group's box: the bubble with only its top line left (report 3a24e49f above). Sides, bottom and
// corners go — but each of those three sides keeps its 1px as transparent, so every row stands at exactly the x,
// the y and the width it had inside the bubble, "the exact same position". The line left standing is the one
// GroupBar's EVERYTHING ELSE word is centred on, so the word, its patch of page and the 8px it rises above the
// line are all as they were. With no corners there is nothing to clip, so the rows' own square ones show
// (overflow), and the run ends on the last row's own borderBottom (EmailItem) — one line where the row's and the
// bubble's bottom edge made two.
// This box is worn by every read run, not only the one under an UNREAD bubble — Calem, report d13a195d: "Show
// email labels in exactly the same format as the email inbox itself, where unread is inside the bubble and
// everything else is not." A view with no unread mail at all (a label, Sent, Archive) used to be the single
// bubble holding the whole list (the `groups` key 'all'), which put read mail inside a bubble in exactly the
// views whose mail is all read. It is drawn as this open group now, wordless: with no unread group above it
// there is nothing for an EVERYTHING ELSE to be else than, so GroupBar is left off and the line it would stand
// on carries no patch of page. Drafts and Snoozed, which split nothing by unread, keep the single bubble.
const GROUP_OPEN: React.CSSProperties = {
  borderRadius: 0,
  borderTop: emailCardLine,
  borderRight: '1px solid transparent',
  borderBottom: '1px solid transparent',
  borderLeft: '1px solid transparent',
  overflow: 'visible',
};
// The UNREAD group's box while it holds the top of the list (Calem, report 452b3a0d: "When scrolling down
// the page, keep the unread email, if any, in a fixed position and only scroll everything else, provided
// there is at least 50% of the vertical space available between unread and everything else combined").
// Both groups used to stand in the one scrolling pane, so UNREAD slid out of sight with the rest of the
// list. It was first held there with position: sticky, which leaves the box IN the scroller — and that is
// what Calem came back to in the 2nd opening: "The unread emails (bubble) still moves up to close the gap
// between the top bubble and the UNREAD bubble when you scroll. Keep the UNREAD bubble completely still."
// A box held that way only locks once it has reached the top of the scrollport, so the first notch of the
// wheel carried the bubble up through its own GROUP_GAP_TOP of page before it caught — and that 17px rise
// is exactly the gap between the toolbar bubble and the UNREAD bubble that Calem watched close.
// So the block is not held inside the scroller at all now. While the gate below allows it, the unread
// group is rendered as a still block of the list column, between the toolbar bubble and the scrolling
// pane, and the pane is left holding the read group alone. Nothing under it scrolls, so nothing can move
// it by any amount; the read rows run up to its bottom edge, where the pane clips them.
// It is the same box the group is inside the pane — GROUP_WRAP_UNREAD / GROUP_WRAP_WIDE_UNREAD, the same
// GroupBar word on its top edge, the same page around it (it was GROUP_WRAP / GROUP_WRAP_WIDE until report
// d8e36715 gave UNREAD its own gap above) — so at rest the list is drawn exactly where it was. GROUP_HELD adds
// the one thing being a flex item of the column calls for: flexShrink 0, so a long run of unread mail
// keeps its own height and takes it off the pane instead of being squeezed.
// Out of the pane it needs no fill of its own (the column it stands on is that same --bg-surface, and
// nothing passes behind it any more) and no zIndex — which also gives an unread row's Add-to-lead window
// (AddToLeadModal) and lead hover card back the plain stacking they had before any of this.
// The two groups stand in different boxes now, so their margins no longer collapse into the one gap: the
// read group's 27 is made up of the held block's own EMAIL_GAP below it plus GROUP_WRAP_READ_HELD's top
// margin above (report 3a24e49f's 2nd opening still measures the same on screen).
// It is held only while the block would take no more than UNREAD_PIN_MAX of the list column's height —
// Calem's proviso, so the read list always keeps the other half of the window (holdUnread in EmailList).
// A taller run of unread mail than that is left in the pane and scrolls away with everything else, as it
// always did. The COLUMN is what that half is measured against, never the pane: taking the block out of
// the pane makes the pane shorter, so a gate measured there would hold and release the same block forever.
const GROUP_HELD: React.CSSProperties = { flexShrink: 0 };
const UNREAD_PIN_MAX = 0.5;

// The word that heads a group of rows in the list: in capitals, 13px, in `color`, sitting ON the top border of
// its own bubble — centred on it, on a patch of the page's own white, so that border reads as the two lines the
// word has always stood between. UNREAD heads the unread group, EVERYTHING ELSE the read rows.
// It was a row of its own inside the bubble, its two lines drawn by hand under the bubble's own top edge, so
// every group showed two lines where one would do (Calem, report 75cd6e0e, 4th opening: "change the unread line
// and everything else line to move into the top border of that bubble instead of being a separate line").
// It is placed out of the bubble and above it, since the bubble clips (GROUP_BUBBLE), and it takes no mouse, so
// the top of the first row under it is still clicked and dragged as any other row is.
// Calem (Bugify #810a6232: "Make the gray line one shade darker and change the word "read" to
// "everything else" remaining in capitals. Add another bar like this above the unread section …
// Increase the size of the words … by 2pt"): there was one bar, READ, between the two groups only —
// 10.5px words between --border lines.
// The word wears the open mailbox's colour, the one on its badge in the sidebar, as its unread rows
// do (Bugify #df98616c, 2nd opening: "Make the words "UNREAD" and "EVERYTHING ELSE" blue as well").
// It had stayed grey, --text-tertiary (Bugify #2e8da37f: "Change the highlighted sections in each
// email client to that email theme color excluding the 'unread' and 'everything else' text, keep
// this as a gray color"), and grey it still is with no single mailbox open, where the rows wear
// several mailboxes' colours (groupColor in EmailList). The lines it stands between are now the
// bubble's own border, which wears that colour too, at 51% (emailCardLine); the pair it drew itself
// wore the mailbox's colour at full strength, and --border-strong with no mailbox open.
// EVERYTHING ELSE stands well below the last unread row (Bugify #df98616c: "Bring the "Everything
// Else" separator down 20 pixels further from any/all 'Unread' Emails"), and UNREAD now stands the
// same distance below the toolbar bubble — one GROUP_GAP_TOP of page above every group bubble.
// UNREAD's own 7px of room went with the bar: the word is on the bubble's edge now, and the page
// above the bubble is its room (it sat 7px down inside it, Calem, report ed55dfa3).
function GroupBar({ label, color }: { label: string; color: string }) {
  return (
    <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1, padding: '0 8px', background: 'var(--bg-surface)', fontSize: 13, lineHeight: '16px', fontWeight: 700, color, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
      {label}
    </div>
  );
}

// ── The search bubble's filter panel (Calem, report 22bdcef3: "allow a drop-down box when you click in
// the search bubble, where you can search a specific time in history. You can search emails with an
// attachment. You can search emails that include certain words etc = Very similar to the functionality
// that you would have in Gmail") ─────────────────────────────────────────────────────────────────────
// Gmail's own boxes, in Gmail's order, dressed as the Edit label window's filter (Modals/
// LabelEditModal) so the two filters read alike: a 104px caption beside each field. What is typed in
// the bubble itself keeps working exactly as before — the boxes narrow it further, they don't replace
// it — and an untouched panel changes nothing at all.
// The panel is position: fixed, like the list's lead hover card: the toolbar sits inside panes that
// clip (MailApp), so a panel laid out inside it would be cut off at the first row of emails.
const FILTER_BOXES = [
  ['from', 'eml_flt_from', 'eml_flt_addr_ph'],
  ['to', 'eml_flt_to', 'eml_flt_addr_ph'],
  ['subject', 'eml_flt_subject', 'eml_flt_words_ph'],
  ['hasWords', 'eml_flt_has_words', 'eml_flt_words_ph'],
  ['notWords', 'eml_flt_not_words', 'eml_flt_words_ph'],
] as const;
// The date window, as the panel offers it: no date limit, one of three windows reaching back from
// today, or two dates typed ('custom' — the only setting that isn't sent as `within`).
const WITHIN_LABEL: Record<string, string> = { week: 'eml_flt_last_week', month: 'eml_flt_last_month', year: 'eml_flt_last_year' };
const fltInput: React.CSSProperties = { width: '100%', padding: '6px 9px', fontSize: 12.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' };
const fltRow: React.CSSProperties = { display: 'grid', gridTemplateColumns: '104px minmax(0, 1fr)', alignItems: 'center', gap: '4px 8px' };
const fltCaption: React.CSSProperties = { fontSize: 12, color: 'var(--text-secondary)' };

// Only the boxes that are filled in — the object the list sends, and what the chips are drawn from.
function cleanFilter(d: EmailFilter): EmailFilter {
  const out: EmailFilter = {};
  const put = (k: 'from' | 'to' | 'subject' | 'hasWords' | 'notWords') => { const v = (d[k] || '').trim(); if (v) out[k] = v; };
  put('from'); put('to'); put('subject'); put('hasWords'); put('notWords');
  if (d.hasAttachment) out.hasAttachment = true;
  if (d.within) out.within = d.within;              // a window wins; the two dates are then ignored
  else { if (d.dateFrom) out.dateFrom = d.dateFrom; if (d.dateTo) out.dateTo = d.dateTo; }
  return out;
}

function FilterPanel({ at, filter, onApply, onClose }: {
  at: { top: number; right: number };
  filter: EmailFilter;
  onApply: (f: EmailFilter) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [f, setF] = useState<EmailFilter>(filter);
  // Two dates typed rather than a window — remembered on its own, so the boxes stay open while they
  // are still empty.
  const [custom, setCustom] = useState(!filter.within && !!(filter.dateFrom || filter.dateTo));
  const ref = useRef<HTMLDivElement>(null);
  const set = (patch: Partial<EmailFilter>) => setF(p => ({ ...p, ...patch }));
  const apply = () => { onApply(cleanFilter(f)); onClose(); };

  // A click anywhere else closes the panel and leaves the list as it is; so does Escape, taken in the
  // capture phase so the list's own Escape (clear the selection) can't fire behind it. The chevron
  // itself is not "anywhere else": it closes the panel on its own click, and closing here first would
  // only let that click open it again.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      if (ref.current?.contains(el) || el?.closest?.('[data-eml-filter-toggle]')) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); };
  }, [onClose]);

  return (
    <div ref={ref} style={{ position: 'fixed', top: at.top, right: at.right, zIndex: 4000, width: 340, maxWidth: 'calc(100vw - 16px)', background: 'var(--bg-surface)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('eml_flt_hint')}</div>
      {FILTER_BOXES.map(([k, caption, ph]) => (
        <div key={k} style={fltRow}>
          <span style={fltCaption}>{t(caption)}</span>
          <input style={fltInput} value={f[k] || ''} maxLength={200} placeholder={t(ph)}
            onChange={e => { const v = e.target.value; setF(p => ({ ...p, [k]: v })); }}
            onKeyDown={e => { if (e.key === 'Enter') apply(); }} />
        </div>
      ))}
      <div style={fltRow}>
        <span style={fltCaption}>{t('eml_flt_date')}</span>
        <select style={fltInput} value={f.within || (custom ? 'custom' : '')}
          onChange={e => {
            const v = e.target.value;
            setCustom(v === 'custom');
            set(v === 'custom'
              ? { within: undefined }
              : { within: (v || undefined) as EmailFilter['within'], dateFrom: undefined, dateTo: undefined });
          }}>
          <option value="">{t('eml_flt_any_time')}</option>
          <option value="week">{t('eml_flt_last_week')}</option>
          <option value="month">{t('eml_flt_last_month')}</option>
          <option value="year">{t('eml_flt_last_year')}</option>
          <option value="custom">{t('eml_flt_custom')}</option>
        </select>
        {custom && (
          <div style={{ gridColumn: 2, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <input type="date" style={{ ...fltInput, flex: 1, minWidth: 0 }} value={f.dateFrom || ''} onChange={e => set({ dateFrom: e.target.value })} />
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>–</span>
            <input type="date" style={{ ...fltInput, flex: 1, minWidth: 0 }} value={f.dateTo || ''} onChange={e => set({ dateTo: e.target.value })} />
          </div>
        )}
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-primary)', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!f.hasAttachment} onChange={e => set({ hasAttachment: e.target.checked })} />
        {t('eml_flt_attachment')}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 4 }}>
        {/* Clear empties every box and puts the whole folder back on the list, panel still open. */}
        <button onClick={() => { setCustom(false); setF({}); onApply({}); }}
          style={{ padding: '6px 14px', fontSize: 12.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit' }}>
          {t('eml_flt_clear')}
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={apply}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 18px', fontSize: 12.5, fontWeight: 600, borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
          <i className="ti ti-search" style={{ fontSize: 13 }} aria-hidden />{t('eml_flt_apply')}
        </button>
      </div>
    </div>
  );
}

// The AI card over the list (report d38cea86): the mail is asked once the box has held two or more words and stood
// still this long, or at once on Enter. Thai and Chinese are written without spaces, so there a run of letters long
// enough to be two words counts as well: nine Thai code points (vowel marks among them) or five Chinese characters.
const AI_WAIT_MS = 1200;
const AI_WORDS = /\S\s+\S|[一-鿿]{5}|[ก-๿]{9}/;

export default function EmailList({ splitScreen, onSetSplitScreen, toolbarHost, listLeft = 0 }: { splitScreen: boolean; onSetSplitScreen: (v: boolean) => void; toolbarHost?: HTMLElement | null; listLeft?: number }) {
  const { t } = useTranslation();
  const { emails: rawEmails, totalEmails, isLoadingMore, loadMoreEmails, activeEmailId, activeAccountId, activeFolder, searchQuery, isLoading,
          setSearchQuery, emailFilter, setEmailFilter, loadEmails, loadEmail, conversationView, setConversationView, emailDensity, setEmailDensity,
          replyAllDefault, setReplyAllDefault, undoSendSecs, setUndoSendSecs, setActiveEmail, archiveEmail, deleteEmail, deleteEmails, markRead, markUnread, toggleStar, setStar, me, labelOverrides, accounts, mailboxFilter } = useStore();
  const [editingDraft, setEditingDraft] = useState<Email | null>(null);
  // Where the filter panel hangs while it is open (null = closed): under the search bubble, lined up
  // with its right-hand end. Measured when it opens — the toolbar strip doesn't scroll.
  const [filterAt, setFilterAt] = useState<{ top: number; right: number } | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  // The AI card (report d38cea86): null = no card, and the list is then exactly what it always was. None of this
  // touches the search itself, which still goes out on the store's own wait or on Enter. aiRun numbers the questions
  // as the store's emailsRun numbers the list's loads, and a change of words or mailbox takes the next number, so an
  // answer to words no longer in the box is dropped whole.
  const [aiCard, setAiCard] = useState<(EmailAiAnswer & { looking?: boolean }) | null>(null);
  const aiRun = useRef(0);
  const aiAsked = useRef(false);   // these words have gone out, so asking again (the wait, then Enter) adds nothing
  const aiWords = searchQuery.trim();
  const askAi = () => {
    // A tab held to some mailboxes with none of them open lists nothing (loadEmails); asked with no mailbox, this
    // would read every mailbox the person has.
    if (aiAsked.current || !AI_WORDS.test(aiWords) || (mailboxFilter && !activeAccountId)) return;
    aiAsked.current = true;
    const run = ++aiRun.current;
    setAiCard({ looking: true, answer: '', sources: [] });
    api.emailAiAnswer(aiWords, activeAccountId)
      .then(r => { if (run === aiRun.current) setAiCard(r.answer ? r : null); })
      .catch(() => { if (run === aiRun.current) setAiCard(null); });
  };
  useEffect(() => {
    aiRun.current++;
    aiAsked.current = false;
    setAiCard(null);
    const wait = setTimeout(askAi, AI_WAIT_MS);
    return () => clearTimeout(wait);
  }, [aiWords, activeAccountId]); // eslint-disable-line
  const closeAiCard = () => { aiRun.current++; aiAsked.current = false; setAiCard(null); };
  // A source on the list opens as its row does, a conversation as a conversation. One off the list (another folder,
  // or a question whose words no email holds whole, which leaves the list empty) has no row for the reading pane to
  // find, so it is pinned there from the full email, as a row that falls off the list stays pinned (getActiveEmail).
  const openAiSource = async (id: string) => {
    const row = rawEmails.find(e => e.id === id) ?? rawEmails.find(e => (e.threadIds || []).includes(id));
    if (row) { setActiveEmail(row.id); return; }
    const full = await loadEmail(id);
    if (full) useStore.setState({ activeEmailId: id, activeEmailPin: full });
  };
  // The attachment chip whose file is being previewed over the page, if any.
  const [previewAtt, setPreviewAtt] = useState<{ emailId: string; att: Attachment } | null>(null);
  const isDraftsView = activeFolder === 'drafts';
  // Snoozed keeps the server's order (soonest wake-up first) — no unread-first regrouping there.
  const isSnoozedView = activeFolder === 'snoozed';

  // Merged Unread/All view: unread messages float to the top of the normal list (in their existing
  // relative order), read ones sit below a separator. The store loads EVERY unread row of the view
  // along with the page (loadEmails), so this group holds what the sidebar badge counts, not only
  // the unread among the newest 50. Drafts keep their natural order (no meaningful unread state).
  const keepOrder = isDraftsView || isSnoozedView;
  const unreadEmails = useMemo(() => keepOrder ? [] : rawEmails.filter(e => e.unread), [rawEmails, keepOrder]);
  const readEmails = useMemo(() => keepOrder ? [] : rawEmails.filter(e => !e.unread), [rawEmails, keepOrder]);
  const emails = useMemo(
    () => (keepOrder || !unreadEmails.length || !readEmails.length) ? rawEmails : [...unreadEmails, ...readEmails],
    [keepOrder, unreadEmails, readEmails, rawEmails],
  );
  // The UNREAD / EVERYTHING ELSE words (GroupBar): the open mailbox's colour, the one on its badge in
  // the sidebar (teal for an unknown colour, as the badge); grey --text-tertiary with no single mailbox
  // open, as they have always been there — a line's colour is too faint to read a word in.
  const openAcct = accounts.find(a => a.id === activeAccountId);
  const groupColor = openAcct ? (ACCOUNT_COLORS[openAcct.color] || ACCOUNT_COLORS.teal).accent : 'var(--text-tertiary)';

  // ── UNREAD held at the top of the list (Calem, report 452b3a0d — see GROUP_HELD) ───────────────────
  // Whether the unread group is taken out of the scrolling pane and stood still above it. The block it
  // would take is its own height plus the GROUP_GAP_TOP_UNREAD of page above it (report d8e36715 and its
  // follow-on d52103ad put that gap at 16, and the proviso counts the whole block the read list gives up),
  // and it is held only while
  // that is no more than UNREAD_PIN_MAX of the list column — Calem's proviso that the read list keeps at
  // least half the window. false is also the state with no unread group on screen at all.
  // The measure is against the whole COLUMN (colRef), not the pane: the pane is what is left of the column
  // once the held block is out of it, so measuring there would shrink the very number the gate is read
  // from and a block near the line would be held, released and held again without end.
  // One ResizeObserver watches both boxes: the unread block grows and shrinks as mail arrives, is read,
  // is fetched by Load more or is re-spaced by Density, and the column's own height moves with the window
  // and with split screen.
  const colRef = useRef<HTMLDivElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  const [unreadBox, setUnreadBox] = useState<HTMLDivElement | null>(null);
  const [holdUnread, setHoldUnread] = useState(false);
  useEffect(() => {
    const col = colRef.current;
    if (!unreadBox || !col) { setHoldUnread(false); return; }
    const measure = () => {
      const visible = col.clientHeight;
      const block = GROUP_GAP_TOP_UNREAD + unreadBox.offsetHeight;
      setHoldUnread(visible > 0 && block <= visible * UNREAD_PIN_MAX);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(unreadBox);
    ro.observe(col);
    return () => ro.disconnect();
  }, [unreadBox]);

  // ── Multi-select + keyboard ────────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [bulkLead, setBulkLead] = useState(false);
  const lastClickedRef = useRef<string | null>(null);
  const canLead = me?.role === 'admin' || !!me?.modules?.includes('leads');
  const selectMode = selected.size > 0;
  const allSelected = emails.length > 0 && selected.size === emails.length;

  // Reset selection/focus whenever the folder or account changes.
  useEffect(() => { setSelected(new Set()); setFocusedId(null); lastClickedRef.current = null; }, [activeFolder, activeAccountId]);
  // Drop ids that no longer exist after a reload (e.g. archived rows leave the view).
  useEffect(() => { setSelected(prev => { if (!prev.size) return prev; const live = new Set(emails.map(e => e.id)); const n = new Set(Array.from(prev).filter(id => live.has(id))); return n.size === prev.size ? prev : n; }); }, [emails]);

  const toggleSelect = (id: string, shift: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (shift && lastClickedRef.current) {
        const ids = emails.map(e => e.id);
        const a = ids.indexOf(lastClickedRef.current), b = ids.indexOf(id);
        if (a >= 0 && b >= 0) { const [lo, hi] = a < b ? [a, b] : [b, a]; for (let i = lo; i <= hi; i++) next.add(ids[i]); }
      } else { next.has(id) ? next.delete(id) : next.add(id); }
      return next;
    });
    lastClickedRef.current = id;
    setFocusedId(id);
  };
  const clearSel = () => setSelected(new Set());
  const selectAll = () => setSelected(allSelected ? new Set() : new Set(emails.map(e => e.id)));
  // Apply an action to the selection (or, if none selected, the keyboard-focused email).
  // Row ids → every email id in those conversations (list actions act on the WHOLE thread).
  const expandThreads = (rowIds: string[]): string[] => {
    const map = new Map(emails.map(e => [e.id, e] as const));
    return rowIds.flatMap(id => { const r = map.get(id); return r?.threadIds?.length ? r.threadIds : [id]; });
  };
  const applyTo = async (fn: (id: string) => Promise<void>, ids?: string[]) => {
    const rows = ids || (selected.size ? Array.from(selected) : (focusedId ? [focusedId] : []));
    if (!rows.length) return;
    const targets = expandThreads(rows);
    for (const id of targets) { try { await fn(id); } catch { /* keep going */ } }
    clearSel();
  };
  // Drag-to-label: dragging a row that's part of the selection carries the whole selection, any
  // other row just itself — expanded to every message of each conversation, like the actions above.
  const dragIds = (id: string) => Array.from(new Set(expandThreads(selected.has(id) ? Array.from(selected) : [id])));
  // Delete = confirm once → rows leave the list at once; any the server can't delete come back (see
  // store.deleteEmails). Errors surface only as a non-blocking toast.
  const bulkDelete = () => {
    const rows = selected.size ? Array.from(selected) : (focusedId ? [focusedId] : []);
    if (!rows.length) return;
    const targets = expandThreads(rows);   // deleting a conversation deletes every message in it
    if (!window.confirm(t('em_del_confirm', { n: targets.length }))) return;
    clearSel();
    void deleteEmails(targets);   // fire-and-forget — the store takes the rows out at once
  };
  const bulkStar = async () => {
    const rows = selected.size ? Array.from(selected) : (focusedId ? [focusedId] : []);
    const map = new Map(emails.map(e => [e.id, e] as const));
    const on = rows.some(id => !map.get(id)?.starred);   // any unstarred → star them all
    for (const id of expandThreads(rows)) { try { await setStar(id, on); } catch { /* ignore */ } }
    clearSel();
  };

  // Keyboard shortcuts — active when the email module is mounted and focus isn't in a field/modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (!el || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable) return;
      // A preview open over the list: its keys (Esc closes it) must not archive / delete rows behind it —
      // this list's own, or one opened from the reading pane's attachments (data-att-preview). The
      // filter panel is the same: while it is open its keys belong to it.
      if (editingDraft || bulkLead || previewAtt || filterAt || document.querySelector('[data-att-preview]')) return;
      if (!emails.length) return;
      const ids = emails.map(x => x.id);
      const curIdx = focusedId ? ids.indexOf(focusedId) : -1;
      const moveFocus = (delta: number, extend: boolean) => {
        const ni = curIdx < 0 ? (delta > 0 ? 0 : ids.length - 1) : Math.min(ids.length - 1, Math.max(0, curIdx + delta));
        const nid = ids[ni];
        if (extend && focusedId) { setSelected(prev => { const n = new Set(prev); n.add(focusedId); n.add(nid); return n; }); }
        setFocusedId(nid);
        document.getElementById('eml-' + nid)?.scrollIntoView({ block: 'nearest' });
      };
      const k = e.key;
      if (k === 'ArrowDown' || k === 'j') { e.preventDefault(); moveFocus(1, e.shiftKey); }
      else if (k === 'ArrowUp' || k === 'k') { e.preventDefault(); moveFocus(-1, e.shiftKey); }
      else if (k === 'Enter' || k === 'o') { if (focusedId) setActiveEmail(focusedId); }
      else if (k === 'x') { e.preventDefault(); if (focusedId) toggleSelect(focusedId, false); }
      else if ((k === 'a' || k === 'A') && (e.metaKey || e.ctrlKey)) { e.preventDefault(); selectAll(); }
      else if (k === 'Escape') { clearSel(); }
      else if (isDraftsView) { /* no row actions in Drafts */ }
      else if (k === 'e') { e.preventDefault(); applyTo(archiveEmail); }
      else if (k === '#' || k === 'Delete' || k === 'Backspace') { e.preventDefault(); bulkDelete(); }
      else if (k === 's') { e.preventDefault(); bulkStar(); }
      else if (k === 'I') { e.preventDefault(); applyTo(markRead); }
      else if (k === 'U') { e.preventDefault(); applyTo(markUnread); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [emails, focusedId, selected, editingDraft, bulkLead, previewAtt, filterAt, isDraftsView]); // eslint-disable-line

  const selectedEmails = emails.filter(e => selected.has(e.id));

  // Open a draft for editing — fetch the FULL body first (list only has a preview)
  const openDraft = async (email: Email) => {
    const full = await loadEmail(email.id);
    setEditingDraft(full || email);
  };

  const isDrafts = activeFolder === 'drafts';
  // Drafts/Trash/Snoozed are always shown flat (never threaded) — the toggle would have nothing to do
  // there. Snoozed keeps the server's order: soonest wake-up first.
  const canGroupThreads = !isDrafts && activeFolder !== 'trash' && activeFolder !== 'snoozed';
  // The select-all checkbox + its caption stand where the folder name used to; the folder name
  // only remains where there's nothing to select (Drafts, an empty list).
  const canSelectAll = !isDrafts && emails.length > 0;
  const folderLabel: Record<string, string> = {
    inbox: 'Inbox', sent: 'Sent', starred: 'Starred',
    archive: 'Archive', drafts: 'Drafts', trash: 'Trash', spam: t('spam'), snoozed: t('eml_snoozed'), scheduled: t('eml_scheduled'),
  };
  // A view this map doesn't name is one of the sidebar's labels, whose own name the folder caption
  // below falls back to — and whose rows all carry folder 'archive' (see leftBar in EmailItem).
  const isLabelView = !folderLabel[activeFolder];
  // Whatever the filter panel is narrowing the list by, as one small chip each beside the bubble —
  // the only sign on the page that the list is showing less than the folder holds. A chip's × sends
  // the OTHER boxes back on their own, so one condition can be dropped without reopening the panel.
  // One chip covers the whole date setting, a window or two dates alike.
  const chips: Array<{ key: string; label: string; rest: EmailFilter }> = [];
  const flt = emailFilter;
  if (flt.from) chips.push({ key: 'from', label: `${t('eml_flt_from')}: ${flt.from}`, rest: { ...flt, from: undefined } });
  if (flt.to) chips.push({ key: 'to', label: `${t('eml_flt_to')}: ${flt.to}`, rest: { ...flt, to: undefined } });
  if (flt.subject) chips.push({ key: 'subject', label: `${t('eml_flt_subject')}: ${flt.subject}`, rest: { ...flt, subject: undefined } });
  if (flt.hasWords) chips.push({ key: 'hasWords', label: `${t('eml_flt_has_words')}: ${flt.hasWords}`, rest: { ...flt, hasWords: undefined } });
  if (flt.notWords) chips.push({ key: 'notWords', label: `${t('eml_flt_not_words')}: ${flt.notWords}`, rest: { ...flt, notWords: undefined } });
  if (flt.hasAttachment) chips.push({ key: 'att', label: t('eml_flt_attachment'), rest: { ...flt, hasAttachment: false } });
  // Dates read day-first in the tenant's own format (utils/region), as every date in the system does.
  const fltDate = flt.within ? t(WITHIN_LABEL[flt.within])
    : flt.dateFrom && flt.dateTo ? `${fmtDate(flt.dateFrom)} – ${fmtDate(flt.dateTo)}`
      : flt.dateFrom ? t('eml_flt_since', { d: fmtDate(flt.dateFrom) })
        : flt.dateTo ? t('eml_flt_until', { d: fmtDate(flt.dateTo) }) : '';
  if (fltDate) chips.push({ key: 'date', label: fltDate, rest: { ...flt, within: undefined, dateFrom: undefined, dateTo: undefined } });

  // The bulk-action icons are 19.5px, 15% larger again than the 17 they were (Calem, report
  // ba24c1ec: "Make the email icons in the screenshot below 15% larger"); the 17 was that same ask
  // one step earlier (report 30e4962a), when they were 15. The button grows with its glyph, 30 →
  // 34.5, so the ring of space around each icon keeps its proportion; the 8px gaps between the
  // buttons are the ones they had. The taller button reaches 2.25px into the toolbar bubble's 10px
  // padding above and below — the trick the search bubble beside it uses — so it takes the room the
  // 30px one took and the bar still stands 52 tall, ticked rows or not. At its full 34.5 it would
  // push the bar 4.5px taller, and the whole list under it down, the moment a row was ticked.
  // 3rd opening of ba24c1ec: the sizes above are settled and untouched — where the row of icons STARTS is
  // what moves (bulkIndent below). Each icon's own glyph is centred in its button, so it stands 7.5px inside
  // whatever the group's left edge is put on — that ring is the number to spend if the icons are ever
  // asked to come a touch further left or right again.
  const bulkBtn = (icon: string, title: string, onClick: () => void, color = 'var(--text-secondary)') => (
    <button onClick={onClick} title={title} style={{ width: 34.5, height: 34.5, margin: '-2.25px 0', borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
      <i className={`ti ${icon}`} style={{ fontSize: 19.5 }} aria-hidden />
    </button>
  );

  // Select-all tick + its clickable caption (not in Drafts). Written once and drawn in both states: with
  // rows ticked it stands inside the block that carries the icons' indent (bulkIndent below), which begins
  // exactly where this caption begins now, so neither the tick nor its words move by a pixel — "leaving
  // the Select and Deselect All where it is".
  const selectAllBox = canSelectAll && (<>
    <span onClick={selectAll} title={allSelected ? 'Deselect all' : 'Select all (Ctrl/⌘+A)'} style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${selected.size ? 'var(--accent)' : 'var(--border-strong)'}`, background: selected.size ? 'var(--accent)' : 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
      {selected.size > 0 && <i className={`ti ti-${allSelected ? 'check' : 'minus'}`} style={{ fontSize: 11, color: '#fff' }} aria-hidden />}
    </span>
    <span onClick={selectAll} style={{ fontSize: 11.5, fontWeight: 700, lineHeight: 1, color: 'var(--text-primary)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>{t(allSelected ? 'eml_deselect_all' : 'eml_select_all')}</span>
  </>);

  // The toolbar: select-all, the Split screen / Conversation view / Density / Reply all switches, Search and the
  // bulk actions. It is drawn in MailApp's strip across the top of the WHOLE module (toolbarHost) — the
  // sidebar's width as well as the list and the reading pane — instead of inside this column, where with split
  // screen on it was squeezed onto two rows (Calem, Bugify #e0e30d4b) and where it stopped at the sidebar's
  // right edge (Calem, report d8e36715, 3rd opening: "Bring the top bar across to the first column"). Only
  // where it is drawn moves — the selection, the search and the bulk actions stay in this list, and the strip
  // stays put as the list reloads, so Search keeps its focus while typing. The strip is MailApp's first row in
  // both layouts, so this is the fallback's only use: the one render before the host exists.
  // It carries its colour itself, since in the
  // strip it no longer sits on the list: a light grey, as File Storage's toolbar band
  // is and every top-menu page's now (Calem, report 3e769579, 4th opening: "Only the top menu strip
  // on every page turns that same grey then the rest of the page below this menu has a white
  // background"). It was the list's panel colour, --bg-panel, a hair darker. Its line is a shade
  // darker, --border-strong where it was --border, as is the top menu's over it (Calem, report
  // a903bda5, 3rd opening: "Make the gray horizontal lines one shade darker on all page menus like
  // this").
  // That grey is one step down the ramp from the other pages' --c-gray-50: it is --c-gray-100, #EFEFED, and the
  // mailbox sidebar card beside it carries the same token in place of its own --bg-sidebar, so the two blocks
  // read as one fill (report a1e6da79: "Change the email menu block background to one shade darker grey
  // (ticked) and them match the email client block (crossed) to that background color"). The ramp step is the
  // whole move — the border, the radius and the gaps are the ones they already had, --c-gray-50 is still every
  // other page's band, and --bg-sidebar still stands for the screens on it.
  // It is a bubble on the page now (see EMAIL_CARD_R): the grey band kept, TOOLBAR_GAP_TOP of page above it,
  // a gutter down its left, and a line all the way round in place of the one across its bottom — the whole
  // row is one of the four Calem outlined (report 75cd6e0e). Its top gap is its own constant, still 10.
  // Its left gutter is its own too, TOOLBAR_GAP_LEFT's 10, wherever it is drawn in the strip across the module
  // (Calem, report e4ff355f, 4th opening: "This is not done on the left-hand side. The Select All bubble is too
  // far to the left … I line them all up with the block where I have the red line"). The red line is drawn on
  // the pinned sidebar bubble's left edge, which is the sidebar's own EMAIL_GAP, 10 (Sidebar) — and in that
  // strip this bubble stands on the page's left edge, so 10 is what puts its edge on the same line. It had the
  // 3 the bubbles below share, cut from 10 with theirs (report 75cd6e0e, 8th and 9th openings), and so came out
  // 7px to the left of the sidebar card and the shield above it: that 3 is the strip between sidebar and list,
  // which only means anything to a bubble inside this column. So the fallback render, the one before the strip
  // exists, keeps EMAIL_GAP — there the 3 is that strip and must not move. The bubbles below keep their 3 and
  // their RIGHT_GAP, and this one keeps its right edge, so this gutter is the only thing that moves: the bubble
  // is 7px narrower, and a window that only just fitted the bar on one line may now wrap it, by the same rule.
  // Nothing in the top menu moves with it. On his own screenshot the red stroke covers x=8–11, over the sidebar
  // card's border at x=10–12, and the shield's ink starts at 9.75 — the row's padding-left is 0, plus the logo
  // button's 1px border and 4px padding and the 4.75px of transparent margin inside logo.png (its ink box
  // starts 75/512 into a 32.4px image). It is on that line within a quarter pixel already; what made it read as
  // too far right was this bubble sitting 7px to its left, so the logo is left exactly where it is — the whole
  // measurement is written up with it, in Portal's note on that row.
  // No gap under it: the page between it and the UNREAD
  // bubble is that bubble's own, so the two can't double up where the strip and the list sit in panes of
  // their own.
  // Down its right it carries RIGHT_GAP, to stand in from the page's right edge as far as the bubbles below it
  // do (report 3a24e49f, see RIGHT_GAP) — this bubble reaches that edge in both layouts, portaled into
  // MailApp's strip across the module. Only until that strip exists is it drawn inside this column, and only
  // there, with split screen on, is EMAIL_GAP the gutter on this side too.
  const toolbarRight = splitScreen && !toolbarHost ? EMAIL_GAP : RIGHT_GAP;
  const toolbarLeft = toolbarHost ? TOOLBAR_GAP_LEFT : EMAIL_GAP;
  // Where the row of bulk-action icons starts: on the email bubbles' left edge, not beside the "n selected"
  // count (Calem, report ba24c1ec, 3rd opening: "I just want you to shift the icons over to the right,
  // leaving the Select and Deselect All where it is, to line up with the email bubbles"). They stood BULK_GAP
  // plus the bar's own gap after the count, which was their place while this bar was the list column's own;
  // since report d8e36715's 3rd opening the bar runs across the WHOLE module, the sidebar included, so that
  // same 12px put them over the mailbox sidebar instead of over the list they act on.
  // The edge to land on is the list column's left edge plus the bubbles' own EMAIL_GAP of 3. Where that
  // column begins is MailApp's to know — the icon rail while the sidebar is collapsed, else the sidebar's
  // dragged width plus the drag strip — so it hands it over as listLeft, live as that column is dragged or
  // collapsed. This bubble's content starts TOOLBAR_GAP_LEFT + its own 1px line + TOOLBAR_PAD_X in from the
  // page's left edge, and bulkStart is the rest of the way from there out to the bubbles' edge.
  // The icons are put there by giving the block in front of them — the tick, its caption and the count —
  // that much width to fill, less the bar's gap and the icons' own BULK_GAP, rather than by a margin on the
  // icons: a margin is measured from whatever stands before it, so it would have to know how wide "Select
  // all" and "3 selected" run in the language of the day. A width knows nothing about either.
  // It is a floor and never a push: where that block is already the wider — the collapsed rail, whose
  // bubbles' edge of 59 falls inside the caption, a sidebar dragged narrow, a long translation — the indent
  // is ignored and the icons follow the count at the 12px they have always had. So they can only ever move
  // right, and can never be pulled back into (or behind) the words beside them.
  // No indent in the fallback render, the one before MailApp's strip exists: there the bar is drawn inside
  // the list column and so already starts at it.
  const bulkStart = toolbarHost ? Math.max(0, listLeft + EMAIL_GAP - (TOOLBAR_GAP_LEFT + 1 + TOOLBAR_PAD_X)) : 0;
  const bulkIndent = Math.max(0, bulkStart - TOOLBAR_ITEM_GAP - BULK_GAP);
  const toolbar = (
    <div style={{ margin: `${TOOLBAR_GAP_TOP}px ${toolbarRight}px 0 ${toolbarLeft}px`, padding: `10px ${TOOLBAR_PAD_X}px`, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: TOOLBAR_ITEM_GAP, rowGap: 6, border: emailCardLine, borderRadius: EMAIL_CARD_R, minHeight: 52, boxSizing: 'border-box', background: 'var(--c-gray-100)' }}>
      {selectMode ? (
        /* With rows ticked, the tick, its caption and the count are one block of the bar, and the block
           carries the indent that puts the icons after it on the email bubbles' left edge (bulkIndent above).
           They keep the gap between them they have as three, and the block starts where the tick starts
           now, so nothing in it moves. No indent to carry and the width is left off altogether, rather than
           written as a 0 — which would let the bar squash the block instead of measuring it by what is
           in it. */
        <div style={{ display: 'flex', alignItems: 'center', gap: TOOLBAR_ITEM_GAP, ...(bulkIndent ? { minWidth: bulkIndent } : {}) }}>
          {selectAllBox}
          <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-primary)' }}>{selected.size} {t('eml_selected')}</span>
        </div>
      ) : (
        <>
          {selectAllBox}
          {!canSelectAll && <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{folderLabel[activeFolder] || labelOverrides[activeAccountId || '']?.[activeFolder]?.name || activeFolder}</span>}
          {/* Split screen on/off */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 10 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('eml_split_screen')}</span>
            <div title={t('eml_split_screen')} style={{ display: 'inline-flex', borderRadius: 999, border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
              {([[true, 'eml_split_on'], [false, 'eml_split_off']] as const).map(([val, key]) => (
                <button key={String(val)} onClick={() => onSetSplitScreen(val)} style={{ padding: '4px 11px', fontSize: 11.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: splitScreen === val ? 'var(--accent)' : 'transparent', color: splitScreen === val ? '#fff' : 'var(--text-secondary)' }}>
                  {t(key)}
                </button>
              ))}
            </div>
          </div>
          {/* Conversation view on/off — on (default) groups same-thread messages into one row;
              off shows every message as its own flat row. Not shown in Drafts/Trash, which are
              always flat. Unread-first sorting + the Read separator below apply either way. */}
          {canGroupThreads && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('eml_conv_view')}</span>
              <div title={t('eml_conv_view')} style={{ display: 'inline-flex', borderRadius: 999, border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
                {([[true, 'eml_split_on'], [false, 'eml_split_off']] as const).map(([val, key]) => (
                  <button key={String(val)} onClick={() => setConversationView(val)} style={{ padding: '4px 11px', fontSize: 11.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: conversationView === val ? 'var(--accent)' : 'transparent', color: conversationView === val ? '#fff' : 'var(--text-secondary)' }}>
                    {t(key)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Row density — Default, Comfortable (more air per row) or Compact (tighter rows; no
              preview line in split screen). Every folder; the choice is remembered on this browser. */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('eml_density')}</span>
            <div title={t('eml_density')} style={{ display: 'inline-flex', borderRadius: 999, border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
              {([['default', 'eml_density_default'], ['comfortable', 'eml_density_comfortable'], ['compact', 'eml_density_compact']] as const).map(([val, key]) => (
                <button key={val} onClick={() => setEmailDensity(val)} style={{ padding: '4px 11px', fontSize: 11.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: emailDensity === val ? 'var(--accent)' : 'transparent', color: emailDensity === val ? '#fff' : 'var(--text-secondary)' }}>
                  {t(key)}
                </button>
              ))}
            </div>
          </div>
          {/* Reply all on/off, in the room after Density (Calem, report 643e2141): on, every Reply button — the
              reading pane's, each message's in a conversation, the Leads and Starred viewers' — is left out (opening
              #2), so Reply All is how to answer: it goes to the sender, To and Cc, less the mailbox's own address,
              and anyone who shouldn't get it is removed in the compose window before sending. An email from one
              sender with no one else on it still goes back to that sender only. Off (the default) keeps Reply as it
              always was; Reply All and Forward stay either way. Every folder; per person. */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('eml_reply_all_default')}</span>
            <div title={t('eml_reply_all_default_tip')} style={{ display: 'inline-flex', borderRadius: 999, border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
              {([[true, 'eml_split_on'], [false, 'eml_split_off']] as const).map(([val, key]) => (
                <button key={String(val)} onClick={() => setReplyAllDefault(val)} style={{ padding: '4px 11px', fontSize: 11.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: replyAllDefault === val ? 'var(--accent)' : 'transparent', color: replyAllDefault === val ? '#fff' : 'var(--text-secondary)' }}>
                  {t(key)}
                </button>
              ))}
            </div>
          </div>
          {/* Unsend on/off, in the room after Reply all (Calem, report eae213ee: "Put an option in the top menu
              for users to to unsend emails Unsend On / Off (if On = [select] 5s / 10s / 20s)"). The wait itself
              was already built (report ef1507ed) — a sent email sits for those seconds with a countdown and an
              Undo button before it goes — but the only way to set it was the ▾ beside the composer's Send Now,
              which is out of sight until someone is already writing an email. Off (the default) posts at once.
              It is the SAME setting as that menu's, undoSendSecs: both write it through setUndoSendSecs, so the
              two always read alike and the choice is the person's own, on every browser and every folder.
              On, from Off, takes the shortest wait the menu offers (UNSEND_ON_SECS) and shows the seconds pill
              beside it; On while already on keeps the chosen seconds, so pressing it twice changes nothing.
              Off remembers nothing — back On returns to that shortest wait, as the Send menu's Off always did. */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('eml_unsend')}</span>
            <div title={t('eml_undo_hint')} style={{ display: 'inline-flex', borderRadius: 999, border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
              {([[true, 'eml_split_on'], [false, 'eml_split_off']] as const).map(([val, key]) => {
                const on = (undoSendSecs > 0) === val;
                return (
                  <button key={String(val)} onClick={() => setUndoSendSecs(val ? (undoSendSecs || UNSEND_ON_SECS) : 0)} style={{ padding: '4px 11px', fontSize: 11.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: on ? 'var(--accent)' : 'transparent', color: on ? '#fff' : 'var(--text-secondary)' }}>
                    {t(key)}
                  </button>
                );
              })}
            </div>
            {/* The seconds, a pill of their own in the same shape — shown only while Unsend is On, since Off
                has no wait to pick. The waits are the Send menu's own list, less its Off. */}
            {undoSendSecs > 0 && (
              <div title={t('eml_undo_hint')} style={{ display: 'inline-flex', borderRadius: 999, border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
                {UNDO_SEND_SECS.filter(n => n > 0).map(n => (
                  <button key={n} onClick={() => setUndoSendSecs(n)} style={{ padding: '4px 11px', fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', border: 'none', cursor: 'pointer', background: undoSendSecs === n ? 'var(--accent)' : 'transparent', color: undoSendSecs === n ? '#fff' : 'var(--text-secondary)' }}>
                    {t('eml_undo_secs', { n })}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      {/* Bulk actions as one group, so in a narrow column they wrap together — after the "n selected"
          count and in front of Search, which keeps the right end of the bar, not at that end themselves
          (Calem, Bugify #a375cb1a screenshot-2). BULK_GAP from the block of three before them, which is the
          gap they had from the count alone and is now their floor: the indent that block carries stands them
          on the email bubbles' left edge instead, wherever the mailbox sidebar leaves it (bulkIndent above —
          report ba24c1ec's 3rd opening). With that indent taken, the bar has less free room than it had, so a
          narrow window wraps this whole group, and then Search, onto a line of its own. */}
      {selectMode && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginLeft: BULK_GAP }}>
          {bulkBtn('ti-archive', 'Archive (e)', () => applyTo(archiveEmail))}
          {bulkBtn('ti-trash', 'Delete (#)', () => bulkDelete(), '#E24B4A')}
          {bulkBtn('ti-mail-opened', 'Mark read (Shift+I)', () => applyTo(markRead))}
          {bulkBtn('ti-mail', 'Mark unread (Shift+U)', () => applyTo(markUnread))}
          {bulkBtn('ti-star', 'Star (s)', bulkStar, '#F5A623')}
          {canLead && bulkBtn('ti-target-arrow', t('ld_add_to'), () => setBulkLead(true), '#6D28D9')}
          {bulkBtn('ti-x', 'Clear (Esc)', clearSel)}
        </div>
      )}
      {/* Search — at the right end of the bar, at most 250px: about 30% of the room it used to fill
          after the switches (Calem, Bugify #e0e30d4b screenshot-7). Its own auto margin pushes it
          there, rows ticked or not — the bulk actions in front of it stay at the left end of the bar, on
          the email bubbles' edge (bulkIndent above).
          It only narrows (to 160px) or drops to its own line when the bar runs short of room. No
          refresh button beside it any more (Calem, Bugify #594776d4 screenshot-12): the sidebar's
          Sync bubble syncs the open mailbox. It stays at this one spot whether rows are ticked or not
          (the bulk-action slot before it is kept even when empty): each keystroke reloads the list,
          which can untick every row mid-typing, and a remounted box would drop the focus.
          It is a round coloured bubble (Calem, Bugify #cba01676, 6th opening: "Do the same (colored bubble search)
          for quotation/invoice/receipts, email/calendar and petty cash"), dressed as Bank Statement's search box:
          round ends, a 1px line, 5px above and below and 12px semibold — in the open mailbox's colour, the one on its
          badge in the sidebar, as the reading pane's round buttons are: its pale wash (--accent-bg), its line (the
          colour at 35%, 51% since — see below) and words in it, the placeholder's too (mf-ph-inherit) — the
          magnifier in it 12px in and the words 30px in, clear of it. The clear × wears it too, and the words still
          stop 28px short of the right end for it. Calem 2026-10-01 (Bugify #87ed6d43: "Change the email theme color to the email client color"): it
          wore the top menu's Email purple (menuPill('email')) whichever mailbox was open; before #cba01676 it was an
          8px-cornered white box with the faint grey outline, its magnifier and × grey.
          Its line is the colour at 51% (Calem, report 0fb815da: "Make the bubble border one shade darker for everything
          on the purple menu pages (email/contacts/calendar)"): a quarter of the way on from the 35% to the solid colour,
          the step the top menu's lines took for "one shade darker" (0.35 → 0.51, see tabPalette), as the reading pane's
          bubbles and the sidebar's Sync pill took too.
          It has Filing's search box's size now and reads "Search email…", where it read "Search emails…" (Calem,
          report 48c2197c, 2nd opening: "Do the same for every search bar. Put the name inside … Search email"):
          13.5px words where they were 12, 7px above and below them where it was 5, the magnifier 14.5px where it was
          13 and 16px in where it was 12, the words 36px in where they were 30; they stop 43px short of the right-hand
          end, which was 28px until the filter chevron joined the × there (report 22bdcef3, below).
          It narrows to 180px where it narrowed to 160, so the bigger words still fit. It
          reaches 3px into the bar's padding above and below, so it takes the room it took. Its colours stay the open
          mailbox's: "color-coded to that menu page" is what it already is, Email's colour being the mailbox's since
          #87ed6d43, whose screenshot circled this bubble in the purple as one of the things to change. */}
      {/* The bubble keeps the right end of the bar; the filter panel's chips sit beside it, in front of
          it, and drop onto their own line when the bar runs short of room (Calem, report 22bdcef3).
          The chevron at the bubble's own right-hand end opens the panel — same bubble, as asked — and
          the clear × steps 19px in to make room for it, so neither covers the other. */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 6, flex: 1, minWidth: 180, margin: '-3px 0 -3px auto' }}>
        {chips.map(c => (
          <span key={c.key} title={c.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 200, padding: '2px 5px 2px 9px', borderRadius: 999, border: '1px solid color-mix(in srgb, var(--accent) 51%, transparent)', background: 'var(--accent-bg)', color: 'var(--accent)', fontSize: 11, fontWeight: 600 }}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.label}</span>
            <button onClick={() => setEmailFilter(c.rest)} title={t('eml_flt_chip_remove')}
              style={{ border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <i className="ti ti-x" style={{ fontSize: 11 }} aria-hidden />
            </button>
          </span>
        ))}
        <div ref={searchRef} style={{ position: 'relative', flex: '1 1 180px', minWidth: 180, maxWidth: 250 }}>
          <i className="ti ti-search" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 14.5, color: 'var(--accent)' }} aria-hidden />
          {/* The words go out 3 seconds after the last key (store, SEARCH_WAIT_MS — Calem, report 2695ff3b:
              "wait for a 3- or 4-second pause in case the user types in extra characters"), so a part-typed
              name is never searched for. Enter searches at once, as the Jobs box does, for anyone who does
              not want to sit out the wait. */}
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { loadEmails(); askAi(); } }}
            placeholder={t('eml_search_email')}
            className="mf-ph-inherit"
            style={{ width: '100%', padding: '7px 43px 7px 36px', fontSize: 13.5, fontWeight: 600, borderRadius: 999, border: '1px solid color-mix(in srgb, var(--accent) 51%, transparent)', outline: 'none', background: 'var(--accent-bg)', color: 'var(--accent)' }}
          />
          {/* Emptying the box brings the whole mailbox back at once — clearing a search is a finished
              action, not half-typed words, so it does not sit out the 3-second wait. */}
          {searchQuery && (
            <button onClick={() => { setSearchQuery(''); loadEmails(); }} style={{ position: 'absolute', right: 25, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <i className="ti ti-x" style={{ fontSize: 12 }} aria-hidden />
            </button>
          )}
          <button
            onClick={() => {
              if (filterAt) { setFilterAt(null); return; }
              const r = searchRef.current?.getBoundingClientRect();
              if (r) setFilterAt({ top: Math.round(r.bottom + 6), right: Math.max(8, Math.round(window.innerWidth - r.right)) });
            }}
            title={t('eml_flt_title')}
            data-eml-filter-toggle
            style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}
          >
            <i className={`ti ti-chevron-${filterAt ? 'up' : 'down'}`} style={{ fontSize: 14 }} aria-hidden />
          </button>
        </div>
      </div>
      {filterAt && (
        <FilterPanel at={filterAt} filter={emailFilter} onApply={setEmailFilter} onClose={() => setFilterAt(null)} />
      )}
    </div>
  );

  // The rows in their groups: the unread ones in a bubble (GROUP_BUBBLE) with UNREAD on its top edge, everything
  // else under a line of its own (GROUP_OPEN) with EVERYTHING ELSE on that. Which rows sit in which and in what
  // order is exactly what the one flat run held — `emails` is unreadEmails followed by readEmails.
  // That rule holds in every view this pane draws, not the Inbox alone — Calem, report d13a195d: "Show email
  // labels in exactly the same format as the email inbox itself, where unread is inside the bubble and
  // everything else is not." A label's mail is usually all read, and a view with no unread row at all used to
  // be the single bubble holding the whole list (the `groups` key 'all'), so a label — and Sent, and Archive —
  // drew every row INSIDE a bubble where the Inbox draws its read rows in the open. Those rows are read rows,
  // so they are now the read group standing on its own: the open line and the read wrapper, the same box the
  // Inbox's read run stands in. It carries no EVERYTHING ELSE word, because that word names the rows as the
  // ones that are not unread and there is no unread group above them here to be else than.
  // Drafts and Snoozed make no unread split at all (keepOrder — unreadEmails and readEmails are empty there and
  // the server's own order is the one that matters), so those two keep the single bubble they have always had.
  // A view whose mail is all unread is one bubble too, UNREAD heading it alone, as it did.
  const groups: { key: string; bar: React.ReactNode; rows: Email[] }[] =
    keepOrder
      ? [{ key: 'all', bar: null, rows: emails }]
      : unreadEmails.length > 0
        ? [
            { key: 'unread', bar: <GroupBar label={t('eml_unread')} color={groupColor} />, rows: unreadEmails },
            // The read group's extra room above its bubble (Calem, Bugify #df98616c: "Bring the 'Everything Else'
            // separator down 20 pixels further from any/all 'Unread' Emails") is folded into GROUP_GAP_TOP, which
            // every bubble now carries, so UNREAD stands the same distance below the toolbar bubble.
            ...(readEmails.length > 0
              ? [{ key: 'read', bar: <GroupBar label={t('eml_everything_else')} color={groupColor} />, rows: readEmails }]
              : []),
          ]
        : [{ key: 'read', bar: null, rows: readEmails }];

  // The unread group when it is the still block above the pane rather than a group inside it (GROUP_HELD):
  // only while the gate allows (holdUnread) and only where the rows are drawn at all — a loading view or an
  // empty one has no list to hold the top of, and the pane shows its spinner or its "No emails" alone.
  const heldUnread = holdUnread && !isLoading && emails.length > 0 ? groups.find(g => g.key === 'unread') : undefined;
  const paneGroups = heldUnread ? groups.filter(g => g !== heldUnread) : groups;

  // A wheel over the held block has nothing under the pointer to scroll — it is out of the pane, and the
  // column around it is clipped, not scrolled. So it is handed to the pane, and wheeling anywhere over the
  // list moves the read rows exactly as it did when the one pane held everything. Lines and pages are what
  // a mouse wheel reports outside the trackpad's pixels, so they are turned into pixels first.
  const wheelToPane = (e: React.WheelEvent<HTMLDivElement>) => {
    const pane = paneRef.current;
    if (!pane) return;
    const step = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? pane.clientHeight : 1;
    pane.scrollTop += e.deltaY * step;
  };

  // One group drawn: the box it stands in, its word on that box's top edge (GroupBar) and its rows inside
  // the bubble. The held UNREAD block and the groups left in the pane come from this one markup, so the
  // held block is the same box, in the same place, as the group it was. `held` says the unread group is
  // standing out of the pane: it carries GROUP_HELD itself, and the read group takes the wrappers whose
  // top margin is the rest of the gap the two no longer collapse into (GROUP_WRAP_READ_HELD).
  // The UNREAD group wears its own wrappers (GROUP_WRAP_UNREAD, 16 above instead of 17 — report d8e36715 and
  // its follow-on d52103ad) in both states, so the bubble stands at that same 16 whether it is in the pane or
  // held above it. The single bubble Drafts and Snoozed are drawn as ('all') is the only box still on
  // GROUP_GAP_TOP's 17; a view with no unread mail is a read group like any other (see `groups`), so it takes
  // the read wrappers and their 27 and nothing here reads its key differently.
  const groupBox = (g: { key: string; bar: React.ReactNode; rows: Email[] }, held: boolean) => (
    <div
      key={g.key}
      ref={g.key === 'unread' ? setUnreadBox : undefined}
      onWheel={held && g.key === 'unread' ? wheelToPane : undefined}
      style={
        g.key === 'read'
          ? held
            ? (splitScreen ? GROUP_WRAP_READ_HELD : GROUP_WRAP_WIDE_READ_HELD)
            : (splitScreen ? GROUP_WRAP_READ : GROUP_WRAP_WIDE_READ)
          : g.key === 'unread'
            ? held
              ? { ...(splitScreen ? GROUP_WRAP_UNREAD : GROUP_WRAP_WIDE_UNREAD), ...GROUP_HELD }
              : (splitScreen ? GROUP_WRAP_UNREAD : GROUP_WRAP_WIDE_UNREAD)
            : (splitScreen ? GROUP_WRAP : GROUP_WRAP_WIDE)
      }
    >
      {g.bar}
      <div style={g.key === 'read' ? GROUP_OPEN : GROUP_BUBBLE}>
        {g.rows.map(email => (
          <EmailItem
            key={email.id}
            email={email}
            isActive={email.id === activeEmailId}
            isDrafts={isDrafts}
            isSnoozedView={isSnoozedView}
            isLabelView={isLabelView}
            onEditDraft={openDraft}
            selected={selected.has(email.id)}
            focused={focusedId === email.id}
            selectMode={selectMode}
            onToggleSelect={toggleSelect}
            dragIds={dragIds}
            splitScreen={splitScreen}
            density={emailDensity}
            onPreviewAtt={setPreviewAtt}
          />
        ))}
      </div>
    </div>
  );

  // The pane carries the page's own white, --bg-surface, so the EMAIL_GAP between the bubbles reads as page,
  // the way the Quotation and Bank lists stand their cards on it (CARD_INSET / cardLine in Peak). It was the
  // panel grey, --bg-panel, a hair darker, which the flat rows ran edge to edge over.
  // It carries no line down its right either: that 1px --border stood alongside the drag strip (DragHandle in
  // MailApp), so the two together were a grey band between these bubbles and the reading pane — the very thing
  // Calem is pointing at in report 75cd6e0e's 4th opening. The strip still drags the two columns apart.
  return (
    <div ref={colRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', overflow: 'hidden' }}>
      {/* The toolbar goes into MailApp's strip across the whole module whenever that strip is there — in both
          layouts now, so the bar runs over the sidebar too (report d8e36715's 3rd opening, see `toolbar`). It
          is drawn here, at the top of this column, only while the strip has yet to exist. */}
      {toolbarHost ? createPortal(toolbar, toolbarHost) : toolbar}

      {/* The AI card (report d38cea86): above the first found email wherever that stands, so above a held UNREAD
          block too, and out of the scrolling pane, so the answer stays in sight as the rows move under it. It wears
          the search bubble's wash and the list bubbles' line and corners, and stands where the UNREAD bubble's top
          does. A long answer scrolls inside it rather than pushing the list off the page. */}
      {aiCard && (
        <div style={{ flexShrink: 0, maxHeight: 220, overflowY: 'auto', display: 'flex', alignItems: 'flex-start', gap: 8, margin: `${GROUP_GAP_TOP_UNREAD}px ${splitScreen ? EMAIL_GAP : RIGHT_GAP}px 0 ${EMAIL_GAP}px`, padding: '8px 10px 8px 12px', borderRadius: EMAIL_CARD_R, border: emailCardLine, background: 'var(--accent-bg)' }}>
          <i className={aiCard.looking ? 'ti ti-loader' : 'ti ti-sparkles'} style={{ fontSize: 15, lineHeight: '18px', color: 'var(--accent)', flexShrink: 0, ...(aiCard.looking ? { display: 'inline-block', animation: 'spin 1s linear infinite' } : {}) }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: '18px' }}>
            {aiCard.looking ? (
              <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{t('eml_ai_looking')}</span>
            ) : (
              <>
                <div style={{ color: 'var(--accent)', fontWeight: 700 }}>{t('eml_ai_answer')}</div>
                <div style={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{aiCard.answer}</div>
                {aiCard.sources.map(s => (
                  <button key={s.id} onClick={() => openAiSource(s.id)} title={s.subject} style={{ display: 'block', maxWidth: '100%', marginTop: 3, padding: 0, border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', textAlign: 'left', fontSize: 12, textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[s.from, s.subject, formatDate(new Date(s.date * 1000))].filter(Boolean).join(' · ')}
                  </button>
                ))}
              </>
            )}
          </div>
          <button onClick={closeAiCard} title={t('eml_ai_close')} style={{ border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <i className="ti ti-x" style={{ fontSize: 14, lineHeight: '18px' }} aria-hidden />
          </button>
        </div>
      )}

      {/* The UNREAD group held still above the scrolling pane, out of it altogether, while the gate allows
          (heldUnread / GROUP_HELD above — report 452b3a0d's 2nd opening). It stands its GROUP_GAP_TOP_UNREAD
          below the toolbar bubble — 16 since report d52103ad, the same wherever the group is drawn — and
          nothing it stands in scrolls, so the wheel cannot move it by so much as the pixel Calem watched it
          rise. */}
      {heldUnread && groupBox(heldUnread, true)}

      {/* The pane the rest of the list scrolls in — all of it while nothing is held, the read group alone
          while UNREAD is. Nothing overlaps it now, so it needs no scroll-padding for the keyboard. */}
      <div ref={paneRef} style={{ flex: 1, overflowY: 'auto' }}>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
            <i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} aria-hidden />
          </div>
        ) : emails.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
            <i className="ti ti-inbox" style={{ fontSize: 32, color: 'var(--text-tertiary)', marginBottom: 8 }} aria-hidden />
            <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
              {isDrafts ? 'No drafts saved' : 'No emails'}
            </div>
            {isDrafts && (
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                Click "New Email" and save a draft to see it here
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Each group a bubble, its word on the bubble's own top edge (see GROUP_WRAP / GROUP_BUBBLE and
                `groups`) — the word out of the bubble, which clips, and in the box it stands in. The read group
                is the one that isn't a bubble: its top line only (GROUP_OPEN), the rest of its edges transparent
                so its rows don't shift. The wrapper carries RIGHT_GAP down its right where the list runs to the
                page's right edge (GROUP_WRAP_WIDE), EMAIL_GAP inside split screen — both from report 3a24e49f.
                The read group's wrapper stands 10px lower than the plain box (GROUP_WRAP_READ /
                GROUP_WRAP_WIDE_READ, report 3a24e49f's 2nd opening), taking its EVERYTHING ELSE line and all
                its rows down with it, and the UNREAD group's own wrapper on a top of its own (GROUP_WRAP_UNREAD /
                GROUP_WRAP_WIDE_UNREAD, 16 — report d8e36715 and, for that 16, report d52103ad) — so the whole
                list rides that bubble's height on the page and the gap between the two groups is the 27 it was.
                All of it is `groupBox` above, the one markup this and the held UNREAD block are drawn from.
                With the block held, the UNREAD group is not in this run at all (paneGroups) and the read
                group takes the wrappers that make up the same 27 across the two boxes. */}
            {paneGroups.map(g => groupBox(g, !!heldUnread))}
            {/* Load more — 50 at a time until every email in this view is shown. */}
            {!isDrafts && emails.length < totalEmails && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '14px 0 18px' }}>
                <button onClick={() => loadMoreEmails()} disabled={isLoadingMore}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 18px', borderRadius: 999, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--accent)', fontSize: 12.5, fontWeight: 600, cursor: isLoadingMore ? 'default' : 'pointer', opacity: isLoadingMore ? 0.6 : 1 }}>
                  <i className={isLoadingMore ? 'ti ti-loader' : 'ti ti-chevron-down'} style={isLoadingMore ? { display: 'inline-block', animation: 'spin 1s linear infinite' } : undefined} aria-hidden />
                  {isLoadingMore ? t('em_loading_more') : t('em_load_more')}
                </button>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('em_shown_of', { shown: emails.length, total: totalEmails })}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Open ComposeNew pre-filled with draft data */}
      {editingDraft && (
        <ComposeNew
          onClose={() => { setEditingDraft(null); loadEmails(); }}
          draft={editingDraft}
        />
      )}

      {/* An attachment chip's file, previewed over the page */}
      {previewAtt && (
        <AttachmentPreview key={previewAtt.emailId + previewAtt.att.id} emailId={previewAtt.emailId} att={previewAtt.att} onClose={() => setPreviewAtt(null)} />
      )}

      {/* Bulk: add all selected emails to one lead */}
      {bulkLead && selectedEmails.length > 0 && (
        <AddToLeadModal
          emails={selectedEmails.map(e => ({ id: e.id, subject: e.subject, fromName: e.from.name || '', fromEmail: e.from.email || '', date: e.date, bodyText: (e as any).preview || e.bodyOriginal || '' }))}
          onClose={() => setBulkLead(false)}
          onDone={() => { setBulkLead(false); clearSel(); loadEmails(); }}
        />
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
